import { SelectionInfo } from '../types/wordTypes';

/** Only report a live selection read when it is slow enough for a user to feel. */
const SLOW_SELECTION_READ_MS = 400;

/** Queued Original/Current reviewed-text results, resolved after the sync. */
type ReviewedTextProbe = {
  original: { value: string };
  current: { value: string };
} | null;

/** Control-flow marker: the revision probe proved there is nothing to fetch. */
class SkipOoxmlFetch extends Error {}
import { log, logError } from '../../utils/logging';
import { createQrProfiler, logQuickResponseStage } from '../../utils/quickResponseDebug';
import { debugLog, debugWarn } from '../../utils/RemoteDebugLog';
import { TrackChangesParser } from '../utils/TrackChangesParser';
import { pickBestParagraphXmlFromPackage } from '../../features/hybridApply/applyVerify';
import {
  formatOoxmlParagraphForModel,
  stripTrackChangeMarkup,
  type ResolveTrackChangeAuthorSide,
} from '../../utils/trackChangesModelText';
import {
  distinctiveBodySearchSlices,
  isAcceptableParagraphSearchPick,
  isStrongParagraphSearchMatch,
  MIN_CLAUSE_SEARCH_LEN,
  paragraphMatchesAnySearch,
  pickBestParagraphIndex,
} from './paragraphSearchMatch';

/* global Word */

/**
 * Checks if Word.js API is available
 */
function isWordAvailable(): boolean {
  return typeof Word !== 'undefined' && typeof Word.run === 'function';
}

/** Serializes navigation Word.run calls — rapid card clicks must not stack. */
type NavigationDebugContext = Record<string, unknown> & {
  selectFullParagraph?: boolean;
  ooxmlParagraphIndex?: number;
  skipOoxmlScanFallback?: boolean;
  /** Skip the all-paragraphs scan after body.search misses. */
  skipParagraphScanFallback?: boolean;
  /** Cap body.search attempts (default: longest 3). */
  maxSearchAttempts?: number;
};

interface FullDocumentContext {
  fullText: string;
  paragraphs: Array<{ index: number; text: string }>;
}

/** Options for resolving which body.paragraphs entry to mutate (table TC uses OOXML indices). */
export type ParagraphApplyResolveOptions = {
  searchText?: string;
  /**
   * Extra strings to match against (live visible text, reject-all original, etc.).
   * Used when searchText is accepted-text and therefore not a contiguous substring
   * of a redline-heavy paragraph's live text.
   */
  searchAnchors?: string[];
  ooxmlParagraphIndex?: number;
  /** When false, never use paragraphIndex as body.paragraphs index without text verification. */
  trustBodyParagraphIndex?: boolean;
};

export type PrepareParagraphForApplyResult = {
  bodyParagraphIndex: number;
  liveText: string;
  selected: boolean;
  via: 'live-text' | 'content-search' | 'track-change-aware' | 'verified-index';
};

export class DocumentReader {
  private static navigationChain: Promise<unknown> = Promise.resolve();
  private static ooxmlTextsCache: { texts: string[]; at: number } | null = null;
  private static readonly OOXML_TEXTS_CACHE_MS = 8000;

  /** Invalidate after structural edits so indices stay aligned with the live document. */
  static invalidateOoxmlParagraphTextsCache(): void {
    DocumentReader.ooxmlTextsCache = null;
  }

  private static runNavigationQueued<T>(task: () => Promise<T>): Promise<T> {
    if (DocumentReader.navigationDepth > 0) {
      return task();
    }
    const run = async (): Promise<T> => {
      DocumentReader.navigationDepth += 1;
      try {
        return await task();
      } finally {
        DocumentReader.navigationDepth -= 1;
      }
    };
    const next = DocumentReader.navigationChain.then(run, run);
    DocumentReader.navigationChain = next.then(
      () => undefined,
      () => undefined,
    );
    return next;
  }

  private static navigationDepth = 0;

  private static async getCachedOoxmlParagraphTexts(): Promise<string[]> {
    const now = Date.now();
    const cached = DocumentReader.ooxmlTextsCache;
    if (cached && now - cached.at < DocumentReader.OOXML_TEXTS_CACHE_MS) {
      return cached.texts;
    }
    const texts = await DocumentReader.getOoxmlAllParagraphTexts();
    DocumentReader.ooxmlTextsCache = { texts, at: now };
    return texts;
  }

  /**
   * Select paragraph at backend/engine OOXML index (body paragraphs first, then table cells).
   * Matches python-docx extraction order used by Module B identification.
   */
  private static async selectParagraphAtEngineIndex(
    context: Word.RequestContext,
    targetIndex: number,
  ): Promise<boolean> {
    let idx = 0;

    const trySelectIn = async (paragraphs: Word.ParagraphCollection): Promise<boolean> => {
      paragraphs.load('items');
      await context.sync();
      for (const para of paragraphs.items) {
        if (idx === targetIndex) {
          para.load('text');
          await context.sync();
          if (!(para.text || '').trim()) {
            return false;
          }
          para.select();
          await context.sync();
          return true;
        }
        idx += 1;
      }
      return false;
    };

    const body = context.document.body;
    if (await trySelectIn(body.paragraphs)) {
      return true;
    }

    body.tables.load('items');
    await context.sync();
    for (const table of body.tables.items) {
      table.rows.load('items');
      await context.sync();
      for (const row of table.rows.items) {
        row.cells.load('items');
        await context.sync();
        for (const cell of row.cells.items) {
          if (await trySelectIn(cell.body.paragraphs)) {
            return true;
          }
        }
      }
    }

    return false;
  }

  /**
   * Extracts paragraph XML from Word document package OOXML
   * getOoxml() returns the full document package, we need to extract just the paragraph part
   */
  private static extractParagraphXmlFromPackage(packageXml: string): string {
    try {
      // Parse the package XML
      const parser = new DOMParser();
      const packageDoc = parser.parseFromString(packageXml, 'text/xml');
      
      // Find the document part (usually /word/document.xml)
      // Try multiple selector patterns for different namespace formats
      const documentPart = packageDoc.querySelector('pkg\\:part[pkg\\:name="/word/document.xml"]') ||
                          packageDoc.querySelector('part[name="/word/document.xml"]') ||
                          packageDoc.querySelector('[name="/word/document.xml"]') ||
                          Array.from(packageDoc.querySelectorAll('part, pkg\\:part')).find((part: Element) => {
                            const name = part.getAttribute('name') || 
                                        part.getAttribute('pkg:name') ||
                                        (part as any).getAttributeNS('http://schemas.microsoft.com/office/2006/xmlPackage', 'name');
                            return name === '/word/document.xml';
                          });
      
      if (documentPart) {
        // Get the XML content from the part
        const xmlData = documentPart.querySelector('pkg\\:xmlData') ||
                       documentPart.querySelector('xmlData') ||
                       Array.from(documentPart.children).find((child: Element) => {
                         const localName = child.localName || child.tagName.split(':').pop();
                         return localName === 'xmlData';
                       });
        
        if (xmlData) {
          // Extract the inner XML (the actual document XML)
          const serializer = new XMLSerializer();
          let documentXml = '';
          
          // Get all child nodes
          Array.from(xmlData.childNodes).forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              documentXml += serializer.serializeToString(node);
            }
          });
          
          // If we have document XML, try to extract the content
          if (documentXml) {
            const docParser = new DOMParser();
            const docXmlDoc = docParser.parseFromString(documentXml, 'text/xml');
            
            // Helper function to find elements by local name (works with namespaces)
            const findElementByLocalName = (parent: Element | Document, localName: string): Element | null => {
              const walker = docXmlDoc.createTreeWalker(
                parent,
                NodeFilter.SHOW_ELEMENT,
                null
              );
              
              let node: Node | null;
              while (node = walker.nextNode()) {
                const element = node as Element;
                const elementLocalName = element.localName || element.tagName.split(':').pop();
                if (elementLocalName === localName) {
                  return element;
                }
              }
              return null;
            };
            
            // Helper function to find all elements by local name
            const findAllElementsByLocalName = (parent: Element, localName: string): Element[] => {
              const results: Element[] = [];
              const walker = docXmlDoc.createTreeWalker(
                parent,
                NodeFilter.SHOW_ELEMENT,
                null
              );
              
              let node: Node | null;
              while (node = walker.nextNode()) {
                const element = node as Element;
                const elementLocalName = element.localName || element.tagName.split(':').pop();
                if (elementLocalName === localName) {
                  results.push(element);
                }
              }
              return results;
            };
            
            // Try to find the body first (selections might contain multiple paragraphs)
            const body = findElementByLocalName(docXmlDoc, 'body');
            
            if (body) {
              // If body has only one paragraph, return just that paragraph
              const paragraphs = findAllElementsByLocalName(body, 'p');
              if (paragraphs.length === 1) {
                return serializer.serializeToString(paragraphs[0]);
              } else if (paragraphs.length > 1) {
                // Multiple paragraphs - return the body content
                // For selections spanning multiple paragraphs, we return the body
                return serializer.serializeToString(body);
              }
            }
            
            // Try to find a single paragraph element (w:p)
            const paragraph = findElementByLocalName(docXmlDoc, 'p');
            if (paragraph) {
              return serializer.serializeToString(paragraph);
            }
            
            // Fallback: return the document XML
            return documentXml;
          }
        }
      }
      
      // Fallback: if we can't parse the package, try to find paragraph XML directly using regex
      // Sometimes the package might contain the paragraph XML in a different format
      
      // Try to find body content first (for selections spanning multiple paragraphs)
      const bodyMatch = packageXml.match(/<w:body[^>]*>[\s\S]*?<\/w:body>/);
      if (bodyMatch) {
        const bodyXml = bodyMatch[0];
        // If body contains only one paragraph, extract just that paragraph
        const singleParagraphMatch = bodyXml.match(/<w:p[^>]*>[\s\S]*?<\/w:p>/);
        if (singleParagraphMatch && (bodyXml.match(/<w:p/g) || []).length === 1) {
          return singleParagraphMatch[0];
        }
        // Otherwise return the body
        return bodyXml;
      }
      
      // Try to find paragraph XML directly
      const paragraphMatch = packageXml.match(/<w:p[^>]*>[\s\S]*?<\/w:p>/);
      if (paragraphMatch) {
        return paragraphMatch[0];
      }
      
      // Last resort: return empty string
      const hasXmlData = documentPart ? (documentPart.querySelector('pkg\\:xmlData') ||
                                        documentPart.querySelector('xmlData') ||
                                        Array.from(documentPart.children).find((child: Element) => {
                                          const localName = child.localName || child.tagName.split(':').pop();
                                          return localName === 'xmlData';
                                        })) : null;
      
      console.warn('[DocumentReader] Could not extract paragraph XML from package', {
        packageLength: packageXml.length,
        packagePreview: packageXml.substring(0, 1000),
        hasDocumentPart: !!documentPart,
        hasXmlData: !!hasXmlData
      });
      return '';
    } catch (error) {
      console.error('[DocumentReader] Error extracting paragraph XML from package:', error);
      return '';
    }
  }

  /**
   * Unwrap package XML from Paragraph/Range.getOoxml() to inner &lt;w:p&gt; (or &lt;w:body&gt; when needed).
   * Same as private extractParagraphXmlFromPackage — exposed for RedlineInserter accepted-text alignment.
   */
  static paragraphOrBodyXmlFromOoxmlPackage(packageXml: string): string {
    return DocumentReader.extractParagraphXmlFromPackage(packageXml);
  }

  /** Extract inner paragraph XML from a queued getOoxml() result (after context.sync). */
  static paragraphXmlFromGetOoxmlResult(
    ooxmlResult: { value?: string } | null | undefined,
  ): string | null {
    const pkg = ooxmlResult?.value;
    if (typeof pkg !== 'string' || !pkg.length) return null;
    return DocumentReader.paragraphOrBodyXmlFromOoxmlPackage(pkg) || null;
  }

  /**
   * Extract a SINGLE <w:p> element XML from a getOoxml() package — never returns a <w:body>
   * wrapper.
   *
   * This is required for callers that need to feed the result back into
   * `OoxmlPackageWrapper.wrapParagraphInPackage` + `Range.insertOoxml`, because that wrapper
   * embeds its input inside <w:document><w:body>...</w:body></w:document>. If the input is
   * already a <w:body> (multi-paragraph) wrapper, the result is nested <w:body><w:body>...
   * which is invalid OOXML and is rejected by `insertOoxml` on Word Win32 with
   * `GeneralException`.
   *
   * Platform context:
   *   - On Word Mac: `paragraph.getOoxml()` typically returns a package with a single <w:p>,
   *     so `paragraphXmlFromGetOoxmlResult` already works correctly.
   *   - On Word Win32: `paragraph.getOoxml()` can return a package whose <w:body> contains
   *     the target paragraph PLUS surrounding section context (trailing empty paragraph,
   *     <w:sectPr>). The generic extractor returns that entire <w:body>; for callers that
   *     need to feed the result back into `insertOoxml` this is wrong.
   *
   * @param ooxmlResult The result of `paragraph.getOoxml()` (after context.sync()).
   * @param expectedTargetText The plain text of the target paragraph (used to disambiguate when
   *   multiple <w:p> elements are present). Required for robust matching on Win32.
   * @returns The serialized <w:p>...</w:p> XML for the target paragraph, or null if the package
   *   could not be parsed.
   */
  static singleParagraphXmlFromGetOoxmlResult(
    ooxmlResult: { value?: string } | null | undefined,
    expectedTargetText?: string | null,
  ): string | null {
    const pkg = ooxmlResult?.value;
    if (typeof pkg !== 'string' || !pkg.length) return null;
    return DocumentReader.singleParagraphXmlFromPackage(pkg, expectedTargetText) || null;
  }

  /**
   * Extract a SINGLE <w:p> element from a paragraph getOoxml() package.
   * See {@link singleParagraphXmlFromGetOoxmlResult} for rationale.
   */
  static singleParagraphXmlFromPackage(
    packageXml: string,
    expectedTargetText?: string | null,
  ): string {
    if (!packageXml || packageXml.length === 0) return '';

    const normalize = (s: string): string => s.replace(/\s+/g, ' ').trim();
    const target = expectedTargetText != null ? normalize(expectedTargetText) : null;
    const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

    try {
      const parser = new DOMParser();
      const packageDoc = parser.parseFromString(packageXml, 'text/xml');
      const serializer = new XMLSerializer();

      // Collect ALL <w:p> elements in the package, namespace-agnostic.
      // Using getElementsByTagNameNS works regardless of namespace prefix used.
      const pNodes = packageDoc.getElementsByTagNameNS(W_NS, 'p');
      const pElements: Element[] = [];
      for (let i = 0; i < pNodes.length; i++) {
        pElements.push(pNodes.item(i) as Element);
      }

      // Fallback: some packages may not declare the namespace on the root, use localName scan.
      if (pElements.length === 0) {
        const all = packageDoc.getElementsByTagName('*');
        for (let i = 0; i < all.length; i++) {
          const el = all.item(i)!;
          const localName = el.localName || el.tagName.split(':').pop();
          if (localName === 'p') pElements.push(el);
        }
      }

      if (pElements.length === 0) return '';

      if (pElements.length === 1) {
        return serializer.serializeToString(pElements[0]);
      }

      // Multiple <w:p> elements (Win32 paragraph.getOoxml(), or a fat Mac package):
      // pick the paragraph that is this live text. First-non-empty is wrong when a
      // leftover 88-char CR cell sits ahead of the 245-char clause.
      if (target != null && target.length > 0) {
        for (const p of pElements) {
          if (normalize(p.textContent || '') === target) {
            return serializer.serializeToString(p);
          }
        }
        const paraNorms = pElements.map((p) => normalize(p.textContent || ''));
        const picked = pickBestParagraphIndex(paraNorms, target);
        if (isAcceptableParagraphSearchPick(picked.score, target) && picked.index >= 0) {
          return serializer.serializeToString(pElements[picked.index]);
        }
      }

      // No expected text — prefer the first non-empty paragraph (skip sectPr-only siblings).
      for (const p of pElements) {
        const text = normalize(p.textContent || '');
        if (text.length > 0) {
          return serializer.serializeToString(p);
        }
      }

      // Last resort: serialize the first <w:p>.
      return serializer.serializeToString(pElements[0]);
    } catch (error) {
      console.error('[DocumentReader] singleParagraphXmlFromPackage error', error);
      return '';
    }
  }

  /**
   * Queue a reference to body.paragraphs[index] without loading paragraphs.items.
   * Caller must context.sync() before reading isNullObject on the returned paragraph.
   */
  static queueParagraphByIndex(
    context: Word.RequestContext,
    paragraphIndex: number,
  ): Word.Paragraph | null {
    if (paragraphIndex < 0) return null;
    const paragraphs = context.document.body.paragraphs;
    let paragraph = paragraphs.getFirst();
    for (let i = 0; i < paragraphIndex; i++) {
      paragraph = paragraph.getNextOrNullObject();
    }
    paragraph.load('isNullObject');
    return paragraph;
  }

  /**
   * Resolve target paragraph by stable Word id when available, else by index chain.
   * Caller must context.sync() before reading isNullObject on the returned paragraph.
   */
  static queueTargetParagraph(
    context: Word.RequestContext,
    paragraphIndex: number,
    uniqueLocalId?: string | null,
  ): Word.Paragraph | null {
    if (paragraphIndex < 0) return null;
    const id = uniqueLocalId?.trim();
    const docAny = context.document as Word.Document & {
      getParagraphByUniqueLocalId?: (id: string) => Word.Paragraph;
    };
    if (id && typeof docAny.getParagraphByUniqueLocalId === 'function') {
      const byId = docAny.getParagraphByUniqueLocalId(id);
      byId.load('isNullObject');
      return byId;
    }
    return DocumentReader.queueParagraphByIndex(context, paragraphIndex);
  }

  /**
   * Gets the currently selected text from the document
   */
  static async getSelectedText(): Promise<SelectionInfo> {
    if (!isWordAvailable()) {
      // Return empty selection when Word.js is not available (browser testing)
      return {
        text: '',
        isEmpty: true,
        range: undefined,
        ooxml: undefined,
        hasTrackChanges: false,
      };
    }

    try {
      return await Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.load('text,ooxml');
        await context.sync();

        const text = selection.text || '';
        const ooxml = (selection as any).ooxml || '';
        const hasTrackChanges = ooxml.includes('<w:ins') || ooxml.includes('<w:del');

        return {
          text: text,
          isEmpty: !text || text.trim().length === 0,
          range: selection,
          ooxml: ooxml,
          hasTrackChanges: hasTrackChanges,
        };
      });
    } catch (error) {
      logError('Failed to get selected text', error);
      throw error;
    }
  }

  /**
   * Gets text from a specific range
   */
  static async getTextFromRange(range: Word.Range): Promise<string> {
    if (!isWordAvailable()) {
      return '';
    }

    try {
      return await Word.run(async (context) => {
        range.load('text');
        await context.sync();
        return range.text || '';
      });
    } catch (error) {
      logError('Failed to get text from range', error);
      throw error;
    }
  }

  /**
   * Gets the current selection range for later use
   */
  static async getSelectionRange(): Promise<Word.Range | null> {
    if (!isWordAvailable()) {
      return null;
    }

    try {
      return await Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.load('text');
        await context.sync();
        return selection;
      });
    } catch (error) {
      logError('Failed to get selection range', error);
      return null;
    }
  }

  /**
   * Gets the paragraph at the current cursor position
   * This is useful when user clicks but doesn't select text
   */
  static async getParagraphAtCursor(): Promise<SelectionInfo> {
    if (!isWordAvailable()) {
      return {
        text: '',
        isEmpty: true,
        range: undefined,
        ooxml: undefined,
        hasTrackChanges: false,
      };
    }

    try {
      return await Word.run(async (context) => {
        const selection = context.document.getSelection();
        
        // Get the paragraph that contains the selection
        // If selection is empty (just cursor), getFirst() will get the paragraph at cursor
        const paragraph = selection.paragraphs.getFirst();
        paragraph.load('text,ooxml');
        await context.sync();

        const paragraphText = paragraph.text || '';
        const isEmpty = !paragraphText || paragraphText.trim().length === 0;
        const ooxml = (paragraph as any).ooxml || '';
        const hasTrackChanges = ooxml.includes('<w:ins') || ooxml.includes('<w:del');

        return {
          text: paragraphText.trim(),
          isEmpty: isEmpty,
          range: selection,
          ooxml: ooxml,
          hasTrackChanges: hasTrackChanges,
        };
      });
    } catch (error) {
      logError('Failed to get paragraph at cursor', error);
      throw error;
    }
  }

  /**
   * Lightweight selection read for live tracking (SelectionTracker).
   * Reads only the current selection and its containing paragraph — never
   * scans the full document body. paragraphIndex is omitted; call
   * getSelectionOrParagraph() when a body index is required (e.g. before apply).
   */
  static async getSelectionLight(
    onTextReady?: (partial: SelectionInfo) => void,
  ): Promise<SelectionInfo> {
    if (!isWordAvailable()) {
      return {
        text: '',
        isEmpty: true,
        range: undefined,
        ooxml: undefined,
        hasTrackChanges: false,
      };
    }

    try {
      const startedAt = Date.now();
      let textReadyAt = startedAt;
      let ooxmlBytes = 0;
      // Office defers work while the pane's webview is hidden, which shows up as
      // a read that "took" tens of seconds but spent them backgrounded. Record
      // it so a throttled read is never mistaken for a slow one.
      const visibilityAtStart =
        typeof document !== 'undefined' ? document.visibilityState : 'unknown';
      let wasHidden = visibilityAtStart === 'hidden';
      const onVisibilityChange = () => {
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
          wasHidden = true;
        }
      };
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onVisibilityChange);
      }

      return await Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.load('text');
        const paragraph = selection.paragraphs.getFirst();
        paragraph.load('text');

        // Probe for revisions in the same round trip. Comparing the Original and
        // Current reviewed text costs two strings; the OOXML package it lets us
        // skip costs ~280KB and seconds. (WordApi 1.4 — the manifest already
        // requires it; the try/catch covers an older host anyway.)
        let paragraphReviewed: ReviewedTextProbe = null;
        try {
          paragraphReviewed = {
            original: (paragraph as any).getReviewedText('Original'),
            current: (paragraph as any).getReviewedText('Current'),
          };
        } catch {
          paragraphReviewed = null;
        }

        await context.sync();
        textReadyAt = Date.now();

        // The clause is the unit of work, so a partial selection is widened to
        // the paragraph that contains it. Highlighting one sentence and editing
        // it in isolation produced a suggestion that did not fit the clause it
        // came from, and an apply that rewrote less than the user had read.
        const displayText = (paragraph.text || '').trim();
        const isEmpty = displayText.length === 0;

        // Only fetch the package when the range actually carries revisions.
        // Anything uncertain — probe unsupported, threw, or returned something
        // other than two strings — falls through to fetching it, so a wrong
        // guess can never silently drop track-changes rendering.
        const reviewed = paragraphReviewed;
        let revisionsKnownAbsent = false;
        let probeOutcome: 'no revisions' | 'has revisions' | 'probe unavailable' =
          'probe unavailable';
        if (reviewed) {
          try {
            const original = reviewed.original.value;
            const current = reviewed.current.value;
            if (typeof original === 'string' && typeof current === 'string') {
              revisionsKnownAbsent = original === current;
              probeOutcome = revisionsKnownAbsent ? 'no revisions' : 'has revisions';
            }
          } catch {
            revisionsKnownAbsent = false;
            probeOutcome = 'probe unavailable';
          }
        }

        // The text is final here, after one round trip. Hand it over before
        // paying for the OOXML package below, so the pane can show the
        // selection immediately instead of staying blank for the whole read.
        if (onTextReady) {
          try {
            onTextReady({
              text: displayText,
              isEmpty,
              range: undefined,
              ooxml: '',
              hasTrackChanges: false,
              ooxmlPending: !isEmpty && !revisionsKnownAbsent,
            });
          } catch (error) {
            logError('Selection text-ready callback failed', error);
          }
        }

        let ooxml = '';
        try {
          if (revisionsKnownAbsent) {
            throw new SkipOoxmlFetch();
          }
          const ooxmlResult = (paragraph as any).getOoxml();
          await context.sync();
          const ooxmlValue = ooxmlResult?.value;
          if (typeof ooxmlValue === 'string' && ooxmlValue.length > 0) {
            ooxmlBytes = ooxmlValue.length;
            ooxml = DocumentReader.extractParagraphXmlFromPackage(ooxmlValue);
          }
        } catch (error) {
          if (!(error instanceof SkipOoxmlFetch)) {
            // OOXML optional for live selection display
          }
        }

        const hasTrackChanges =
          ooxml.length > 0 && (ooxml.includes('<w:ins') || ooxml.includes('<w:del'));

        // Stay quiet while this is fast; speak up when it is not. The split
        // matters: `text` is one cheap round trip, `ooxml` serializes a whole
        // OOXML package and is the usual reason a selection feels slow.
        if (typeof document !== 'undefined') {
          document.removeEventListener('visibilitychange', onVisibilityChange);
        }
        const totalMs = Date.now() - startedAt;
        if (totalMs >= SLOW_SELECTION_READ_MS) {
          log(
            `[DocumentReader] Slow selection read: ${totalMs}ms total ` +
              `(text ${textReadyAt - startedAt}ms, ooxml ${Date.now() - textReadyAt}ms, ` +
              `package ${revisionsKnownAbsent ? 'skipped' : `${Math.round(ooxmlBytes / 1024)}KB`} ` +
              `[${probeOutcome}], ` +
              `pane ${wasHidden ? 'HIDDEN during read' : 'visible'})`,
          );
        }

        return {
          text: displayText,
          isEmpty,
          range: undefined,
          ooxml,
          hasTrackChanges,
          ooxmlPending: false,
        };
      });
    } catch (error) {
      logError('Failed to get lightweight selection', error);
      throw error;
    }
  }

  /**
   * Gets the current selection or paragraph at cursor if no text is selected
   * This enables click-to-select paragraph functionality
   */
  static async getSelectionOrParagraph(): Promise<SelectionInfo> {
    if (!isWordAvailable()) {
      return {
        text: '',
        isEmpty: true,
        range: undefined,
        ooxml: undefined,
        hasTrackChanges: false,
      };
    }

    try {
      return await Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.load('text');
        await context.sync();

        // The clause is the unit of work, so the highlighted fragment only
        // serves to locate its paragraph — the same rule getSelectionLight
        // follows. Fetching the selection's own OOXML package here was both
        // unused and the expensive part of this read.
        const targetParagraph: any = selection.paragraphs.getFirst();
        targetParagraph.load('text');
        await context.sync();

        // Compute paragraph index. Exact text match misses redline-heavy paragraphs
        // (live text includes struck-through deletions). Prefer Word's uniqueLocalId.
        let paragraphIndex: number | undefined = undefined;
        try {
          const paragraphs = context.document.body.paragraphs;
          paragraphs.load('items');
          await context.sync();

          let targetId = '';
          try {
            (targetParagraph as { load: (prop: string) => void }).load('uniqueLocalId');
            await context.sync();
            targetId = String(
              (targetParagraph as { uniqueLocalId?: string }).uniqueLocalId || '',
            ).trim();
          } catch {
            targetId = '';
          }

          try {
            for (const para of paragraphs.items) {
              para.load('text');
              if (targetId) {
                (para as { load: (prop: string) => void }).load('uniqueLocalId');
              }
            }
            await context.sync();
          } catch {
            for (const para of paragraphs.items) {
              para.load('text');
            }
            await context.sync();
            targetId = '';
          }

          if (targetId) {
            for (let i = 0; i < paragraphs.items.length; i++) {
              const id = String(
                (paragraphs.items[i] as { uniqueLocalId?: string }).uniqueLocalId || '',
              ).trim();
              if (id && id === targetId) {
                paragraphIndex = i;
                break;
              }
            }
          }

          if (paragraphIndex == null) {
            const targetNorm = DocumentReader.normalizeForMatch(targetParagraph.text || '');
            if (targetNorm) {
              for (let i = 0; i < paragraphs.items.length; i++) {
                if (
                  DocumentReader.normalizeForMatch(paragraphs.items[i].text || '') === targetNorm
                ) {
                  paragraphIndex = i;
                  break;
                }
              }
            }
          }

          console.log('[DocumentReader] Computed paragraph index:', paragraphIndex, 'for text:', (targetParagraph.text || '').substring(0, 50));
        } catch (e) {
          console.warn('[DocumentReader] Could not compute paragraph index:', e);
        }

        // No early return for a partial selection: it used to hand back just the
        // highlighted fragment, so Replace rewrote less than the pane had shown.
        // Fall through and return the containing paragraph in every case.

        // Otherwise, return paragraph at cursor – select its entire text so the user sees it selected
        try {
          (targetParagraph as any).getRange('Whole');
          //paraRange.select();
          await context.sync();
        } catch (e) {
          console.warn('[DocumentReader] Could not select paragraph at cursor:', e);
        }

        const paragraphText = targetParagraph.text || '';
        const isEmpty = !paragraphText || paragraphText.trim().length === 0;
        
        // Try to get ooxml using getOoxml() method (required for Word Online)
        let paragraphOoxml = '';
        try {
          // Use getOoxml() method which returns a result object
          const ooxmlResult = (targetParagraph as any).getOoxml();
          await context.sync();
          
          // Access the value property after sync
          const ooxmlValue = ooxmlResult.value;
          
          if (ooxmlValue !== undefined && ooxmlValue !== null && typeof ooxmlValue === 'string' && ooxmlValue.length > 0) {
            // getOoxml() returns the full document package, we need to extract the paragraph XML
            paragraphOoxml = this.extractParagraphXmlFromPackage(ooxmlValue);
            console.log('[DocumentReader] Successfully extracted OOXML from paragraph using getOoxml():', {
              packageLength: ooxmlValue.length,
              paragraphLength: paragraphOoxml.length,
              preview: paragraphOoxml.substring(0, 200)
            });
          } else {
            console.warn('[DocumentReader] Paragraph OOXML from getOoxml() is empty or invalid:', {
              value: ooxmlValue,
              type: typeof ooxmlValue,
              length: ooxmlValue?.length || 0
            });
          }
        } catch (e) {
          console.error('[DocumentReader] Could not get paragraph OOXML using getOoxml() method:', e);
        }
        
        const paragraphHasTrackChanges = paragraphOoxml.length > 0 && 
          (paragraphOoxml.includes('<w:ins') || paragraphOoxml.includes('<w:del'));

        return {
          text: paragraphText.trim(),
          isEmpty: isEmpty,
          range: targetParagraph,
          ooxml: paragraphOoxml,
          hasTrackChanges: paragraphHasTrackChanges,
          paragraphIndex: paragraphIndex,
        };
      });
    } catch (error) {
      logError('Failed to get selection or paragraph', error);
      throw error;
    }
  }

  /**
   * Paragraph text reads are batched: queue `load('text')` then one `sync()` per chunk.
   * Avoids per-paragraph sync (very slow on large docs).
   */
  private static readonly PARAGRAPH_TEXT_LOAD_BATCH_SIZE = 150;

  private static readonly WORDPROCESSING_NS =
    'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

  private static readonly LIST_NUMBER_PREFIX_RE = /^(\d+(?:\.\d+)*)([.)])?\s+/;

  /**
   * Builds accepted ("Accept All Changes") text per paragraph from body package OOXML.
   * Returns null if package parsing fails.
   */
  private static buildAcceptedParagraphTextsFromPackageXml(
    packageXml: string
  ): { texts: string[]; skippedDeletedOnlyParagraphs: number } | null {
    if (!packageXml || !packageXml.trim()) {
      return null;
    }
    try {
      const bodyXml = DocumentReader.extractParagraphXmlFromPackage(packageXml);
      if (!bodyXml) {
        return null;
      }
      const domParser = new DOMParser();
      const xmlDoc = domParser.parseFromString(bodyXml, 'text/xml');
      if (xmlDoc.querySelector('parsererror')) {
        return null;
      }
      const NS_W = DocumentReader.WORDPROCESSING_NS;
      const serializer = new XMLSerializer();
      const allPEls = Array.from(xmlDoc.getElementsByTagNameNS(NS_W, 'p'));
      const parts: string[] = [];
      let skippedDeletedOnlyParagraphs = 0;
      for (let i = 0; i < allPEls.length; i++) {
        const pEl = allPEls[i];
        let pXml = serializer.serializeToString(pEl);
        if (!pXml.includes('xmlns:w=')) {
          pXml = pXml.replace(/^(<[^>]+)>/, `$1 xmlns:w="${NS_W}">`);
        }
        const acceptedText = TrackChangesParser.extractAcceptedText(pXml);
        const acceptedTrimmed = acceptedText.trim();
        const originalTrimmed = TrackChangesParser.extractOriginalText(pXml).trim();

        // After accept-all, deleted-only paragraphs have empty accepted text but Office.js
        // still exposes them as body paragraphs. Skipping them broke index alignment with
        // paragraphs.items — keep an empty slot instead.
        if (!acceptedTrimmed && originalTrimmed) {
          skippedDeletedOnlyParagraphs += 1;
        }

        parts.push(acceptedText);
      }
      return { texts: parts, skippedDeletedOnlyParagraphs };
    } catch {
      return null;
    }
  }

  /**
   * Builds original ("Reject All Changes") text per paragraph from body package OOXML.
   * Symmetric to buildAcceptedParagraphTextsFromPackageXml, but uses extractOriginalText.
   * Skips insertion-only paragraphs (those that vanish after reject-all).
   * Returns null if package parsing fails.
   */
  private static buildOriginalParagraphTextsFromPackageXml(
    packageXml: string
  ): { texts: string[]; skippedInsertionOnlyParagraphs: number } | null {
    if (!packageXml || !packageXml.trim()) {
      return null;
    }
    try {
      const bodyXml = DocumentReader.extractParagraphXmlFromPackage(packageXml);
      if (!bodyXml) {
        return null;
      }
      const domParser = new DOMParser();
      const xmlDoc = domParser.parseFromString(bodyXml, 'text/xml');
      if (xmlDoc.querySelector('parsererror')) {
        return null;
      }
      const NS_W = DocumentReader.WORDPROCESSING_NS;
      const serializer = new XMLSerializer();
      const allPEls = Array.from(xmlDoc.getElementsByTagNameNS(NS_W, 'p'));
      const parts: string[] = [];
      let skippedInsertionOnlyParagraphs = 0;
      for (let i = 0; i < allPEls.length; i++) {
        const pEl = allPEls[i];
        let pXml = serializer.serializeToString(pEl);
        if (!pXml.includes('xmlns:w=')) {
          pXml = pXml.replace(/^(<[^>]+)>/, `$1 xmlns:w="${NS_W}">`);
        }
        const originalText = TrackChangesParser.extractOriginalText(pXml);
        const originalTrimmed = originalText.trim();
        const acceptedTrimmed = TrackChangesParser.extractAcceptedText(pXml).trim();

        // Paragraph exists only as an insertion — it disappears after reject-all.
        if (!originalTrimmed && acceptedTrimmed) {
          skippedInsertionOnlyParagraphs += 1;
          continue;
        }

        parts.push(originalText);
      }
      return { texts: parts, skippedInsertionOnlyParagraphs };
    } catch {
      return null;
    }
  }

  /**
   * Editor-like per-paragraph text from body package OOXML: pending w:ins/w:del stay
   * visible as <ins>/<del> tags instead of accept-all flattening.
   */
  private static buildAgentViewParagraphTextsFromPackageXml(
    packageXml: string,
    resolveAuthorSide?: ResolveTrackChangeAuthorSide,
  ): { texts: string[] } | null {
    if (!packageXml || !packageXml.trim()) {
      return null;
    }
    try {
      const bodyXml = DocumentReader.extractParagraphXmlFromPackage(packageXml);
      if (!bodyXml) {
        return null;
      }
      const domParser = new DOMParser();
      const xmlDoc = domParser.parseFromString(bodyXml, 'text/xml');
      if (xmlDoc.querySelector('parsererror')) {
        return null;
      }
      const NS_W = DocumentReader.WORDPROCESSING_NS;
      const serializer = new XMLSerializer();
      const allPEls = Array.from(xmlDoc.getElementsByTagNameNS(NS_W, 'p'));
      const parts: string[] = [];
      for (let i = 0; i < allPEls.length; i++) {
        const pEl = allPEls[i];
        let pXml = serializer.serializeToString(pEl);
        if (!pXml.includes('xmlns:w=')) {
          pXml = pXml.replace(/^(<[^>]+)>/, `$1 xmlns:w="${NS_W}">`);
        }
        parts.push(formatOoxmlParagraphForModel(pXml, resolveAuthorSide));
      }
      return { texts: parts };
    } catch {
      return null;
    }
  }

  /**
   * Builds per-paragraph text using Office.js paragraphs + listItem.listString.
   * Default: accepted-text semantics (Ask / Proofread).
   * `agentTrackChangeView`: Editor-like <del>/<ins> markup for Agent and Document Review.
   */
  private static async getParagraphTextsWithOfficeNumbering(
    context: Word.RequestContext,
    options?: {
      agentTrackChangeView?: boolean;
      resolveAuthorSide?: ResolveTrackChangeAuthorSide;
    },
  ): Promise<string[]> {
    const startedAt = Date.now();
    const agentView = !!options?.agentTrackChangeView;
    const body = context.document.body;
    const paragraphs = body.paragraphs;
    const bodyOoxmlResult = (body as any).getOoxml();
    paragraphs.load('items');
    await context.sync();
    const officeLoadMs = Date.now() - startedAt;

    const n = paragraphs.items.length;
    const batch = DocumentReader.PARAGRAPH_TEXT_LOAD_BATCH_SIZE;
    const plainTexts: string[] = new Array(n).fill('');
    const listStrings: Array<string | undefined> = new Array(n).fill(undefined);

    const acceptedExtractStart = Date.now();
    const packageXml: string = bodyOoxmlResult.value ?? '';
    const acceptedResult = DocumentReader.buildAcceptedParagraphTextsFromPackageXml(packageXml);
    const acceptedTextsRaw = acceptedResult?.texts ?? null;
    let acceptedTexts = acceptedTextsRaw ? [...acceptedTextsRaw] : null;
    const agentViewRaw =
      agentView
        ? DocumentReader.buildAgentViewParagraphTextsFromPackageXml(
            packageXml,
            options?.resolveAuthorSide,
          )?.texts ?? null
        : null;
    let agentViewTexts = agentViewRaw ? [...agentViewRaw] : null;
    let trimmedTrailingEmptyAccepted = 0;
    const trimTrailingEmpty = (arr: string[] | null, treatMarkupAsContent: boolean): string[] | null => {
      if (!arr) return arr;
      while (arr.length > n) {
        const last = arr[arr.length - 1];
        const visible = treatMarkupAsContent
          ? stripTrackChangeMarkup(last || '').trim()
          : (last || '').trim();
        if (visible.length > 0) break;
        arr.pop();
        trimmedTrailingEmptyAccepted += 1;
      }
      return arr;
    };
    acceptedTexts = trimTrailingEmpty(acceptedTexts, false);
    agentViewTexts = trimTrailingEmpty(agentViewTexts, true);
    const alignedTexts = agentView ? agentViewTexts : acceptedTexts;
    const canUseAcceptedByIndex = !!alignedTexts && alignedTexts.length === n;
    const acceptedExtractMs = Date.now() - acceptedExtractStart;

    for (let start = 0; start < n; start += batch) {
      const end = Math.min(start + batch, n);

      for (let i = start; i < end; i++) {
        const para = paragraphs.items[i] as any;
        para.load('text');
        try {
          const li = para.listItemOrNullObject;
          if (li) {
            li.load('listString,isNullObject');
          }
        } catch {
          // listItemOrNullObject/listString may be unavailable in this host
        }
      }

      await context.sync();

      for (let i = start; i < end; i++) {
        const para = paragraphs.items[i] as any;
        const acceptedText = acceptedTexts?.[i] || '';
        const officeText = typeof para.text === 'string' ? para.text : '';
        const agentText = agentViewTexts?.[i] || '';
        // Agent view: never fall back to unmarked Office.js text (that path treats
        // pending deletions as live obligations). Empty agent/accepted slots stay empty.
        const paragraphText = agentView
          ? (agentText.trim() ? agentText : acceptedText)
          : acceptedText.trim()
            ? acceptedText
            : officeText;
        plainTexts[i] = paragraphText;

        try {
          const li = para.listItemOrNullObject;
          if (li && !li.isNullObject && li.listString != null && String(li.listString).trim() !== '') {
            listStrings[i] = String(li.listString).trim();
          }
        } catch {
          // ignore
        }
      }
    }

    if (!canUseAcceptedByIndex) {
      log('[Perf][ContextBuild] validation_failed', {
        officeParagraphs: n,
        acceptedParagraphs: acceptedTexts?.length ?? 0,
        acceptedParagraphsRaw: acceptedTextsRaw?.length ?? 0,
        agentViewParagraphs: agentViewTexts?.length ?? 0,
        agentTrackChangeView: agentView,
        trimmedTrailingEmptyAccepted,
        skippedDeletedOnlyParagraphs: acceptedResult?.skippedDeletedOnlyParagraphs ?? 0,
        officeLoadMs,
        acceptedExtractMs,
        totalMs: Date.now() - startedAt,
      });

      throw new Error(
        `Accept-all paragraph validation failed: officeParagraphs=${n}, acceptedParagraphs=${acceptedTexts?.length ?? 0}`
      );
    }

    const out: string[] = new Array(n).fill('');
    for (let i = 0; i < n; i++) {
      const cleanText = (plainTexts[i] || '').trim();
      if (!cleanText) continue;
      if (agentView && !stripTrackChangeMarkup(cleanText).trim()) continue;
      const listString = listStrings[i];
      if (listString) {
        const textWithoutLeadingList = cleanText.replace(DocumentReader.LIST_NUMBER_PREFIX_RE, '');
        out[i] = `${listString} ${textWithoutLeadingList}`.trim();
      } else {
        out[i] = cleanText;
      }
    }

    log('[Perf][ContextBuild] success', {
      officeParagraphs: n,
      acceptedParagraphsRaw: acceptedTextsRaw?.length ?? 0,
      acceptedParagraphsAligned: acceptedTexts?.length ?? 0,
      agentViewParagraphs: agentViewTexts?.length ?? 0,
      agentTrackChangeView: agentView,
      trimmedTrailingEmptyAccepted,
      skippedDeletedOnlyParagraphs: acceptedResult?.skippedDeletedOnlyParagraphs ?? 0,
      nonEmptyOutputParagraphs: out.filter((t) => (t || '').trim().length > 0).length,
      officeTextFallbackParagraphs: agentView
        ? 0
        : plainTexts.filter((text, i) => {
            const acceptedText = acceptedTexts?.[i] || '';
            return !acceptedText.trim() && (text || '').trim().length > 0;
          }).length,
      officeLoadMs,
      acceptedExtractMs,
      totalMs: Date.now() - startedAt,
    });

    return out;
  }

  /**
   * Load inner &lt;w:p&gt; OOXML for body.paragraphs indices (same index space as
   * `getFullDocumentContext()`). Used by Document Review to render party redlines
   * the same way MCC Client Request does.
   */
  static async getParagraphOoxmlByIndices(indices: number[]): Promise<Record<number, string>> {
    if (!isWordAvailable() || indices.length === 0) {
      return {};
    }
    const unique = [...new Set(indices.filter((i) => Number.isInteger(i) && i >= 0))];
    if (unique.length === 0) {
      return {};
    }

    const out: Record<number, string> = {};
    const batchSize = 40;
    for (let offset = 0; offset < unique.length; offset += batchSize) {
      const slice = unique.slice(offset, offset + batchSize);
      const batch = await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();
        const n = paragraphs.items.length;
        const queued: Array<{
          index: number;
          paragraph: { text?: string };
          ooxml: { value?: string };
        }> = [];
        for (const index of slice) {
          if (index >= n) continue;
          const paragraph = paragraphs.items[index] as any;
          paragraph.load('text');
          queued.push({
            index,
            paragraph,
            ooxml: paragraph.getOoxml(),
          });
        }
        if (queued.length === 0) {
          return {} as Record<number, string>;
        }
        await context.sync();
        const mapped: Record<number, string> = {};
        for (const item of queued) {
          const xml = DocumentReader.singleParagraphXmlFromGetOoxmlResult(
            item.ooxml,
            item.paragraph.text || '',
          );
          if (xml) mapped[item.index] = xml;
        }
        return mapped;
      });
      Object.assign(out, batch);
    }
    return out;
  }

  /**
   * Shared full-document context builder used by Ask, Proofread, and Document Review
   * findings (accept-all text). Agent uses getFullDocumentContextForAgent() so pending
   * redlines stay visible to the model.
   */
  static async getFullDocumentContext(): Promise<FullDocumentContext> {
    if (!isWordAvailable()) {
      return { fullText: '', paragraphs: [] };
    }

    return Word.run(async (context) => {
      const startedAt = Date.now();
      const numberedParagraphTexts = await DocumentReader.getParagraphTextsWithOfficeNumbering(context);
      const paragraphs = numberedParagraphTexts
        .map((text, index) => ({ index, text: text.trim() }))
        .filter((p) => p.text.length > 0);
      log('[Perf][ContextBuild] getFullDocumentContext_done', {
        fullTextLength: paragraphs.map((p) => p.text).join('\n').length,
        paragraphCount: paragraphs.length,
        totalMs: Date.now() - startedAt,
      });
      return {
        fullText: paragraphs.map((p) => p.text).join('\n'),
        paragraphs,
      };
    });
  }

  /**
   * Agent / tool context: same paragraph indices as getFullDocumentContext(), but
   * pending track changes are visible as <del>/<ins> (Editor view), not accept-all.
   */
  static async getFullDocumentContextForAgent(
    resolveAuthorSide?: ResolveTrackChangeAuthorSide,
  ): Promise<FullDocumentContext> {
    if (!isWordAvailable()) {
      return { fullText: '', paragraphs: [] };
    }

    try {
      return await Word.run(async (context) => {
        const startedAt = Date.now();
        const numberedParagraphTexts = await DocumentReader.getParagraphTextsWithOfficeNumbering(
          context,
          { agentTrackChangeView: true, resolveAuthorSide },
        );
        const paragraphs = numberedParagraphTexts
          .map((text, index) => ({ index, text: text.trim() }))
          .filter((p) => p.text.length > 0);
        log('[Perf][ContextBuild] getFullDocumentContextForAgent_done', {
          fullTextLength: paragraphs.map((p) => p.text).join('\n').length,
          paragraphCount: paragraphs.length,
          totalMs: Date.now() - startedAt,
        });
        return {
          fullText: paragraphs.map((p) => p.text).join('\n'),
          paragraphs,
        };
      });
    } catch (err) {
      log('[Perf][ContextBuild] getFullDocumentContextForAgent_fallback_accept_all', {
        error: err instanceof Error ? err.message : String(err),
      });
      return DocumentReader.getFullDocumentContext();
    }
  }

  /**
   * Builds document context with "Reject All Changes" semantics — insertions removed,
   * deletions kept. This is the clean/original loan agreement text before client redlines.
   *
   * Returns the same { fullText, paragraphs } shape as getFullDocumentContext(), so it
   * can be sent as structured JSON to the backend (same transport as Proofread / Agent).
   * Unlike getFullDocumentContext(), no paragraph-count alignment with Office.js is required
   * because this context is not used for write-back paragraph targeting.
   */
  static async getFullDocumentContextOriginal(): Promise<FullDocumentContext> {
    if (!isWordAvailable()) {
      return { fullText: '', paragraphs: [] };
    }

    return Word.run(async (context) => {
      const startedAt = Date.now();
      const body = context.document.body;
      const bodyOoxmlResult = (body as any).getOoxml();
      await context.sync();

      const packageXml: string = bodyOoxmlResult.value ?? '';
      const result = DocumentReader.buildOriginalParagraphTextsFromPackageXml(packageXml);

      if (!result) {
        log('[Perf][ContextBuild] getFullDocumentContextOriginal: OOXML parse failed, falling back to empty');
        return { fullText: '', paragraphs: [] };
      }

      const paragraphs = result.texts
        .map((text, index) => ({ index, text: text.trim() }))
        .filter((p) => p.text.length > 0);

      log('[Perf][ContextBuild] getFullDocumentContextOriginal_done', {
        fullTextLength: paragraphs.map((p) => p.text).join('\n').length,
        paragraphCount: paragraphs.length,
        skippedInsertionOnlyParagraphs: result.skippedInsertionOnlyParagraphs,
        totalMs: Date.now() - startedAt,
      });

      return {
        fullText: paragraphs.map((p) => p.text).join('\n'),
        paragraphs,
      };
    });
  }

  /**
   * Gets the full text of the entire document as if all track changes were accepted
   * (insertions kept, deletions omitted — matches "Accept All Changes" for context).
   * Uses body OOXML + TrackChangesParser.extractAcceptedText per paragraph.
   */
  static async getFullDocumentText(): Promise<string> {
    if (!isWordAvailable()) {
      return '';
    }

    try {
      const ctx = await DocumentReader.getFullDocumentContext();
      return ctx.fullText;
    } catch (error) {
      logError('Failed to get full document text', error);
      throw error;
    }
  }

  /**
   * Gets the text from the first page of the document
   * Approximates first page by getting first ~5000 characters or first 20 paragraphs
   * Useful for initial entity extraction without processing entire document
   */
  static async getFirstPageText(): Promise<string> {
    if (!isWordAvailable()) {
      return '';
    }

    try {
      return await Word.run(async (context) => {
        const body = context.document.body;
        const paragraphs = body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        const maxParagraphs = 20;
        const maxChars = 5000;
        const count = Math.min(paragraphs.items.length, maxParagraphs);

        for (let i = 0; i < count; i++) {
          paragraphs.items[i].load('text');
        }
        await context.sync();

        let firstPageText = '';
        for (let i = 0; i < count; i++) {
          const paragraphText = paragraphs.items[i].text + '\n';
          if (firstPageText.length + paragraphText.length > maxChars) {
            const remaining = maxChars - firstPageText.length;
            if (remaining > 100) {
              firstPageText += paragraphText.substring(0, remaining);
            }
            break;
          }
          firstPageText += paragraphText;
        }

        return firstPageText.trim();
      });
    } catch (error) {
      logError('Failed to get first page text', error);
      throw error;
    }
  }

  /**
   * Gets the full OOXML of the entire document
   * Useful for extracting track changes across the entire document
   */
  static async getFullDocumentOOXML(): Promise<string> {
    if (!isWordAvailable()) {
      return '';
    }

    try {
      return await Word.run(async (context) => {
        const body = context.document.body;
        
        // Try to get ooxml using getOoxml() method
        try {
          const ooxmlResult = (body as any).getOoxml();
          await context.sync();
          
          const ooxmlValue = ooxmlResult.value;
          
          if (ooxmlValue !== undefined && ooxmlValue !== null && typeof ooxmlValue === 'string' && ooxmlValue.length > 0) {
            // getOoxml() returns the full document package, extract the body XML
            const bodyXml = this.extractParagraphXmlFromPackage(ooxmlValue);
            console.log('[DocumentReader] Successfully extracted full document OOXML:', {
              packageLength: ooxmlValue.length,
              bodyLength: bodyXml.length
            });
            return bodyXml;
          } else {
            console.warn('[DocumentReader] Full document OOXML is empty or invalid');
            return '';
          }
        } catch (e) {
          console.error('[DocumentReader] Could not get full document OOXML:', e);
          return '';
        }
      });
    } catch (error) {
      logError('Failed to get full document OOXML', error);
      throw error;
    }
  }

  /**
   * Gets all paragraphs with their indices
   */
  static async getParagraphsWithIndices(): Promise<Array<{index: number, text: string}>> {
    try {
      const ctx = await DocumentReader.getFullDocumentContext();
      return ctx.paragraphs;
    } catch (error) {
      logError('Failed to get paragraphs with indices', error);
      throw error;
    }
  }

  /**
   * Plain text of every <w:p> in document order (body + table cells), via OOXML.
   * Uses accepted (post-track-change) text so indices align with Module B search anchors.
   */
  static async getOoxmlAllParagraphTexts(): Promise<string[]> {
    if (!isWordAvailable()) return [];
    try {
      return await Word.run(async (context) => {
        const bodyOoxmlResult = (context.document.body as any).getOoxml();
        await context.sync();
        const packageXml: string = bodyOoxmlResult.value ?? '';
        const acceptedResult =
          DocumentReader.buildAcceptedParagraphTextsFromPackageXml(packageXml);
        if (acceptedResult?.texts?.length) {
          return acceptedResult.texts;
        }
        return [];
      });
    } catch (error) {
      logError('Failed to get OOXML paragraph texts', error);
      return [];
    }
  }

  /** OOXML document-order index for the first paragraph matching search text. */
  static async findOoxmlParagraphIndexContainingText(
    searchText: string,
  ): Promise<number | null> {
    const trimmed = searchText?.trim() || '';
    if (!trimmed) return null;

    const allTexts = await DocumentReader.getCachedOoxmlParagraphTexts();
    if (!allTexts.length) return null;

    const searchNorm = DocumentReader.normalizeForMatch(trimmed);
    if (!searchNorm) return null;

    const normalizedParagraphs = allTexts.map((t) =>
      DocumentReader.normalizeForMatch(t),
    );
    const idx = DocumentReader.findParagraphIndexForNormalizedSearch(
      normalizedParagraphs,
      searchNorm,
    );
    return idx >= 0 ? idx : null;
  }

  /**
   * Accepted and client texts of ONE paragraph, from what `paragraph.getOoxml()`
   * returned for it. Word on Windows can return the paragraph together with the
   * paragraphs after it; reading the whole body then gives Sticky a paragraph
   * that holds the next cards' requests too, and the longest of them wins. So
   * only the `<w:p>` whose text is the one Word shows is read.
   */
  static stickyParagraphTextsFromPackage(
    packageXml: string,
    shownText: string,
    clientAuthors: ReadonlySet<string>,
  ): { accepted: string; client: string; paragraphsInPackage: number } {
    const bodyXml = DocumentReader.extractParagraphXmlFromPackage(packageXml);
    const paragraphsInPackage = (bodyXml.match(/<w:p[\s>]/g) || []).length;
    const picked = pickBestParagraphXmlFromPackage(packageXml, shownText);
    // The picker hands the package back when it cannot parse it: read nothing then.
    const pXml = picked && picked !== packageXml ? picked : '';
    if (!pXml) return { accepted: '', client: '', paragraphsInPackage };
    return {
      accepted: TrackChangesParser.extractAcceptedText(pXml).trim(),
      client:
        clientAuthors.size > 0
          ? TrackChangesParser.extractTextAcceptingAuthors(pXml, clientAuthors).trim()
          : '',
      paragraphsInPackage,
    };
  }

  /** Public form of the text normalization every paragraph match here uses. */
  static normalizeTextForMatch(text: string): string {
    return DocumentReader.normalizeForMatch(text);
  }

  /**
   * The paragraph at the cursor, for Sticky: its text as Word shows it (with
   * tracked deletions, where a request that has since been answered still
   * reads), its text with every change accepted — both normalized — and its
   * index in the cached OOXML scan (null when it cannot be placed there).
   */
  static async readCursorParagraphForSticky(clientAuthors: ReadonlySet<string> = new Set()): Promise<{
    shownNorm: string;
    acceptedNorm: string;
    /** As the client left it — their revisions accepted, ours rejected. Empty without client authors. */
    clientNorm: string;
    index: number | null;
  } | null> {
    if (!isWordAvailable()) return null;
    try {
      const { shown, accepted, client } = await Word.run(async (context) => {
        const paragraph = context.document.getSelection().paragraphs.getFirst();
        paragraph.load('text');
        const ooxml = (paragraph as unknown as { getOoxml: () => { value?: string } }).getOoxml();
        await context.sync();
        const shownText = (paragraph.text || '').trim();
        return {
          shown: shownText,
          ...DocumentReader.stickyParagraphTextsFromPackage(ooxml.value ?? '', shownText, clientAuthors),
        };
      });
      const shownNorm = DocumentReader.normalizeForMatch(shown);
      const acceptedNorm = DocumentReader.normalizeForMatch(accepted);
      const clientNorm = DocumentReader.normalizeForMatch(client);
      // The cached scan holds accepted text, so it is looked up with the
      // accepted text: the shown text carries every deleted run and misses it.
      const index = await DocumentReader.findOoxmlParagraphIndexContainingText(accepted || shown);
      return { shownNorm, acceptedNorm, clientNorm, index };
    } catch (error) {
      logError('Failed to read the cursor paragraph for sticky', error);
      return null;
    }
  }

  /**
   * Resolve body.paragraphs index for apply/submit flows.
   * Backend paragraph_index is OOXML document order (includes table cells) and must not
   * be passed directly to body.paragraphs[i] when indices diverge.
   */
  static async resolveParagraphForApply(
    paragraphIndex?: number,
    options?: ParagraphApplyResolveOptions,
  ): Promise<number | null> {
    if (!isWordAvailable()) return null;

    const searchText = (options?.searchText || '').replace(/\s+/g, ' ').trim();
    const ooxmlIdx = options?.ooxmlParagraphIndex;
    const profile = createQrProfiler('resolveParagraphForApply');
    profile.mark('start', {
      searchLen: searchText.length,
      searchSnippet: searchText.slice(0, 80),
      ooxmlParagraphIndex: ooxmlIdx ?? null,
    });

    // Live paragraph.text is the source of truth. body.search misses common
    // Hebrew needles in this document, and leftover clause.paragraph_index
    // values (בדק בית / סעיף 9) must not win just because they are stored.
    // Short CRs (track_changes_18: "הצהרת המממן.") are under the old 15-char
    // gate and must still locate via live-text scan.
    if (searchText.length >= MIN_CLAUSE_SEARCH_LEN) {
      const byLiveText = await DocumentReader.findParagraphIndexContainingText(searchText);
      profile.mark('live-text scan', { hit: byLiveText != null, bodyParagraphIndex: byLiveText });
      if (byLiveText != null) {
        log('DocumentReader.resolveParagraphForApply: resolved via live paragraph text', {
          bodyParagraphIndex: byLiveText,
          ooxmlParagraphIndex: ooxmlIdx ?? null,
          searchSnippet: searchText.slice(0, 120),
        });
        return byLiveText;
      }
    }

    if (searchText.length >= MIN_CLAUSE_SEARCH_LEN) {
      const bySearch = await DocumentReader.resolveBodyParagraphIndexByContentSearch(searchText);
      profile.mark('body.search', { hit: bySearch != null, bodyParagraphIndex: bySearch });
      if (bySearch != null) {
        log('DocumentReader.resolveParagraphForApply: resolved via content search', {
          bodyParagraphIndex: bySearch,
          ooxmlParagraphIndex: ooxmlIdx ?? null,
          searchSnippet: searchText.slice(0, 120),
        });
        return bySearch;
      }

      // Redline-heavy fallback only after live search/scan miss.
      const byTrackChangeAware = await DocumentReader.resolveBodyParagraphIndexByAcceptedText(
        searchText,
        undefined,
        options?.searchAnchors,
      );
      profile.mark('track-change-aware (body.getOoxml)', {
        hit: byTrackChangeAware != null,
        bodyParagraphIndex: byTrackChangeAware,
      });
      if (byTrackChangeAware != null) {
        log('DocumentReader.resolveParagraphForApply: resolved via track-change-aware match', {
          bodyParagraphIndex: byTrackChangeAware,
          ooxmlParagraphIndex: ooxmlIdx ?? null,
          searchSnippet: searchText.slice(0, 120),
        });
        return byTrackChangeAware;
      }
    }

    if (
      options?.trustBodyParagraphIndex !== false &&
      paragraphIndex != null &&
      paragraphIndex >= 0 &&
      !Number.isNaN(Number(paragraphIndex))
    ) {
      const idx = Number(paragraphIndex);
      const verified = await DocumentReader.verifyBodyParagraphIndexMatchesText(
        idx,
        searchText,
      );
      if (verified) {
        log('DocumentReader.resolveParagraphForApply: using verified body index', {
          bodyParagraphIndex: idx,
        });
        return idx;
      }
    }

    profile.mark('failed', {
      paragraphIndex: paragraphIndex ?? null,
      ooxmlParagraphIndex: ooxmlIdx ?? null,
    });
    log('DocumentReader.resolveParagraphForApply: failed', {
      paragraphIndex: paragraphIndex ?? null,
      ooxmlParagraphIndex: ooxmlIdx ?? null,
      searchSnippet: searchText.slice(0, 120),
    });
    return null;
  }

  /**
   * Locate the apply paragraph, select it, and return its live text in as few
   * Word.run trips as the hit path allows. The common case (live-text scan)
   * is one run: load items + texts + select. Fallbacks still locate first,
   * then select+read in a second run — never a third.
   */
  static async resolveSelectAndReadForApply(
    paragraphIndex?: number,
    options?: ParagraphApplyResolveOptions,
  ): Promise<PrepareParagraphForApplyResult | null> {
    if (!isWordAvailable()) return null;

    const searchText = (options?.searchText || '').replace(/\s+/g, ' ').trim();
    const ooxmlIdx = options?.ooxmlParagraphIndex;
    const profile = createQrProfiler('resolveSelectAndReadForApply');
    profile.mark('start', {
      searchLen: searchText.length,
      searchSnippet: searchText.slice(0, 80),
      ooxmlParagraphIndex: ooxmlIdx ?? null,
    });

    return DocumentReader.runNavigationQueued(async () => {
      if (searchText.length >= MIN_CLAUSE_SEARCH_LEN) {
        const byLiveText = await DocumentReader.scanLiveParagraphsForApply(searchText, true);
        profile.mark('live-text scan', {
          hit: byLiveText != null,
          bodyParagraphIndex: byLiveText?.index ?? null,
          selected: byLiveText?.selected ?? false,
        });
        if (byLiveText != null) {
          log('DocumentReader.resolveSelectAndReadForApply: resolved via live paragraph text', {
            bodyParagraphIndex: byLiveText.index,
            ooxmlParagraphIndex: ooxmlIdx ?? null,
            searchSnippet: searchText.slice(0, 120),
            selected: byLiveText.selected,
          });
          return {
            bodyParagraphIndex: byLiveText.index,
            liveText: byLiveText.liveText,
            selected: byLiveText.selected,
            via: 'live-text',
          };
        }

        const bySearch = await DocumentReader.resolveBodyParagraphIndexByContentSearch(searchText);
        profile.mark('body.search', { hit: bySearch != null, bodyParagraphIndex: bySearch });
        if (bySearch != null) {
          const prepared = await DocumentReader.selectAndReadBodyParagraph(bySearch);
          return {
            bodyParagraphIndex: bySearch,
            liveText: prepared.liveText,
            selected: prepared.selected,
            via: 'content-search',
          };
        }

        const byTrackChangeAware = await DocumentReader.resolveBodyParagraphIndexByAcceptedText(
          searchText,
          undefined,
          options?.searchAnchors,
        );
        profile.mark('track-change-aware (body.getOoxml)', {
          hit: byTrackChangeAware != null,
          bodyParagraphIndex: byTrackChangeAware,
        });
        if (byTrackChangeAware != null) {
          const prepared = await DocumentReader.selectAndReadBodyParagraph(byTrackChangeAware);
          return {
            bodyParagraphIndex: byTrackChangeAware,
            liveText: prepared.liveText,
            selected: prepared.selected,
            via: 'track-change-aware',
          };
        }
      }

      if (
        options?.trustBodyParagraphIndex !== false &&
        paragraphIndex != null &&
        paragraphIndex >= 0 &&
        !Number.isNaN(Number(paragraphIndex))
      ) {
        const idx = Number(paragraphIndex);
        const verified = await DocumentReader.verifyBodyParagraphIndexMatchesText(idx, searchText);
        if (verified) {
          const prepared = await DocumentReader.selectAndReadBodyParagraph(idx);
          return {
            bodyParagraphIndex: idx,
            liveText: prepared.liveText,
            selected: prepared.selected,
            via: 'verified-index',
          };
        }
      }

      profile.mark('failed', {
        paragraphIndex: paragraphIndex ?? null,
        ooxmlParagraphIndex: ooxmlIdx ?? null,
      });
      return null;
    });
  }

  private static async selectAndReadBodyParagraph(
    bodyParagraphIndex: number,
  ): Promise<{ liveText: string; selected: boolean }> {
    try {
      return await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();
        if (bodyParagraphIndex < 0 || bodyParagraphIndex >= paragraphs.items.length) {
          return { liveText: '', selected: false };
        }
        const para = paragraphs.items[bodyParagraphIndex];
        para.load('text');
        para.select();
        await context.sync();
        return {
          liveText: (para.text || '').replace(/\s+/g, ' ').trim(),
          selected: true,
        };
      });
    } catch (error) {
      logError('selectAndReadBodyParagraph failed', error);
      return { liveText: '', selected: false };
    }
  }

  private static async verifyBodyParagraphIndexMatchesText(
    bodyParagraphIndex: number,
    searchText: string,
    ooxmlTargetNorm?: string,
  ): Promise<boolean> {
    try {
      return await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();
        if (bodyParagraphIndex < 0 || bodyParagraphIndex >= paragraphs.items.length) {
          return false;
        }
        const para = paragraphs.items[bodyParagraphIndex];
        para.load('text');
        await context.sync();
        const paraNorm = DocumentReader.normalizeForMatch(para.text || '');
        if (!paraNorm) return false;
        const searchNorm = searchText
          ? DocumentReader.normalizeForMatch(searchText)
          : '';
        if (searchNorm && DocumentReader.paragraphMatchesSearchNorm(paraNorm, searchNorm)) {
          return true;
        }
        if (searchNorm && DocumentReader.paragraphSharesDistinctiveAnchor(paraNorm, searchNorm)) {
          return true;
        }
        if (ooxmlTargetNorm && paraNorm === ooxmlTargetNorm) {
          return true;
        }
        return false;
      });
    } catch {
      return false;
    }
  }

  private static async resolveBodyParagraphIndexByContentSearch(
    searchText: string,
    ooxmlTargetNorm?: string,
  ): Promise<number | null> {
    const trimmed = searchText.trim();
    const searchNorm = DocumentReader.normalizeForMatch(trimmed);
    if (!searchNorm) return null;

    const searchAttempts = DocumentReader.buildBodySearchAttempts(searchNorm);
    logQuickResponseStage('profile: body.search attempts', {
      attemptCount: searchAttempts.length,
      attemptLens: searchAttempts.map((a) => a.length),
    });

    for (let attemptIdx = 0; attemptIdx < searchAttempts.length; attemptIdx++) {
      const attempt = searchAttempts[attemptIdx];
      const attemptStartedAt = Date.now();
      try {
        const resolvedIndex = await Word.run(async (context) => {
          const body = context.document.body;
          const searchResults = body.search(attempt, {
            matchCase: false,
            matchWholeWord: false,
          });
          searchResults.load('items');
          await context.sync();
          if (searchResults.items.length === 0) {
            logQuickResponseStage('profile: body.search attempt', {
              attemptIdx,
              attemptLen: attempt.length,
              hits: 0,
              ms: Date.now() - attemptStartedAt,
            });
            return null;
          }

          const paragraphs = body.paragraphs;
          paragraphs.load('items');
          await context.sync();
          for (const para of paragraphs.items) {
            para.load('text');
          }
          await context.sync();

          const normalizedParagraphs = paragraphs.items.map((para) =>
            DocumentReader.normalizeForMatch(para.text || ''),
          );

          const hitNorms: string[] = [];
          for (const range of searchResults.items) {
            const paragraph = range.paragraphs.getFirst();
            paragraph.load('text');
            await context.sync();
            hitNorms.push(DocumentReader.normalizeForMatch(paragraph.text || ''));
          }

          const picked = pickBestParagraphIndex(hitNorms, searchNorm, ooxmlTargetNorm);
          const acceptable = picked.index >= 0 && isAcceptableParagraphSearchPick(picked.score, searchNorm);
          logQuickResponseStage('profile: body.search attempt', {
            attemptIdx,
            attemptLen: attempt.length,
            hits: searchResults.items.length,
            loadedAllParagraphs: normalizedParagraphs.length,
            bestScore: picked.score,
            acceptable,
            ms: Date.now() - attemptStartedAt,
          });
          if (!acceptable) {
            return null;
          }
          const selectedParaNorm = hitNorms[picked.index] || '';

          if (!selectedParaNorm) return null;

          for (let i = 0; i < normalizedParagraphs.length; i++) {
            if (normalizedParagraphs[i] === selectedParaNorm) {
              return i;
            }
          }
          return null;
        });
        if (resolvedIndex != null) return resolvedIndex;
      } catch (error) {
        logQuickResponseStage('profile: body.search attempt FAILED', {
          attemptIdx,
          message: error instanceof Error ? error.message : String(error),
          ms: Date.now() - attemptStartedAt,
        });
        logError('resolveBodyParagraphIndexByContentSearch: body.search failed', error);
      }
    }

    logQuickResponseStage('profile: body.search fallback to live-text scan again', {
      searchLen: searchNorm.length,
    });
    return DocumentReader.findParagraphIndexContainingText(searchNorm);
  }

  /**
   * Resolve a body.paragraphs index when body.search cannot, typically because the
   * paragraph already has pending redlines.
   *
   * Compares the search string against:
   * - live paragraph.text (includes deletions + insertions as Word shows them)
   * - accepted OOXML text (insertions kept, deletions omitted)
   * - original OOXML text (deletions kept, insertions omitted)
   * - distinctive tokens that appear in both views (e.g. English phrases)
   *
   * Trailing empty OOXML paragraphs are trimmed so accepted/original arrays align with
   * body.paragraphs — the same alignment getFullDocumentContext relies on. If they still
   * do not align, live-text + distinctive-anchor matching is used so we never guess an
   * offset index.
   */
  private static async resolveBodyParagraphIndexByAcceptedText(
    searchText: string,
    ooxmlTargetNorm?: string,
    searchAnchors?: string[],
  ): Promise<number | null> {
    const searchNorm = DocumentReader.normalizeForMatch(searchText.trim());
    if (!searchNorm) return null;

    try {
      return await Word.run(async (context) => {
        const t0 = Date.now();
        const body = context.document.body;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const bodyOoxmlResult = (body as any).getOoxml();
        const paragraphs = body.paragraphs;
        paragraphs.load('items');
        await context.sync();
        const firstSyncMs = Date.now() - t0;

        const n = paragraphs.items.length;
        const batch = DocumentReader.PARAGRAPH_TEXT_LOAD_BATCH_SIZE;
        const liveTexts: string[] = new Array(n).fill('');
        const tLive = Date.now();
        for (let start = 0; start < n; start += batch) {
          const end = Math.min(start + batch, n);
          for (let i = start; i < end; i++) {
            paragraphs.items[i].load('text');
          }
          await context.sync();
          for (let i = start; i < end; i++) {
            liveTexts[i] = paragraphs.items[i].text || '';
          }
        }
        const liveBatchMs = Date.now() - tLive;

        const packageXml: string = bodyOoxmlResult.value ?? '';
        logQuickResponseStage('profile: track-change-aware body.getOoxml', {
          paraCount: n,
          firstSyncMs,
          liveBatchMs,
          packageXmlLen: packageXml.length,
          totalMs: Date.now() - t0,
        });
        const acceptedRaw =
          DocumentReader.buildAcceptedParagraphTextsFromPackageXml(packageXml)?.texts ?? null;
        const originalRaw =
          DocumentReader.buildOriginalParagraphTextsFromPackageXml(packageXml)?.texts ?? null;
        const acceptedAligned = DocumentReader.trimTrailingEmptyTexts(acceptedRaw, n);
        const originalAligned = DocumentReader.trimTrailingEmptyTexts(originalRaw, n);

        const views: Array<{ label: string; texts: string[] | null }> = [
          { label: 'live', texts: liveTexts },
          { label: 'accepted', texts: acceptedAligned },
          { label: 'original', texts: originalAligned },
        ];

        const extraNorms = (searchAnchors || [])
          .map((a) => DocumentReader.normalizeForMatch(a || ''))
          .filter((a) => a.length >= MIN_CLAUSE_SEARCH_LEN);

        for (const view of views) {
          if (!view.texts || view.texts.length !== n) continue;
          const norms = view.texts.map((t) => DocumentReader.normalizeForMatch(t || ''));

          const bySearch = DocumentReader.findParagraphIndexForNormalizedSearch(norms, searchNorm);
          if (bySearch >= 0) {
            // OOXML index is a tie-breaker among search hits, not a bypass. Using it first
            // applied clause 7.9 onto a leftover 2500-₪ cell whose body index equaled
            // clause.paragraph_index (94) even though searchText was a different clause.
            if (ooxmlTargetNorm) {
              const ooxmlHit = norms.findIndex((p) => p === ooxmlTargetNorm);
              if (
                ooxmlHit >= 0 &&
                DocumentReader.paragraphMatchesSearchNorm(norms[ooxmlHit], searchNorm)
              ) {
                return ooxmlHit;
              }
            }
            return bySearch;
          }

          for (const extra of extraNorms) {
            const byAnchor = DocumentReader.findParagraphIndexForNormalizedSearch(norms, extra);
            if (byAnchor >= 0) return byAnchor;
          }

          const byToken = DocumentReader.findIndexByDistinctiveAnchors(norms, searchNorm);
          if (byToken >= 0) return byToken;

          for (const extra of extraNorms) {
            const byExtraToken = DocumentReader.findIndexByDistinctiveAnchors(norms, extra);
            if (byExtraToken >= 0) return byExtraToken;
          }
        }

        log('DocumentReader.resolveBodyParagraphIndexByAcceptedText: no match', {
          officeParagraphs: n,
          acceptedParagraphs: acceptedAligned?.length ?? 0,
          originalParagraphs: originalAligned?.length ?? 0,
          searchSnippet: searchNorm.slice(0, 120),
        });
        return null;
      });
    } catch (error) {
      logError('resolveBodyParagraphIndexByAcceptedText: failed', error);
      return null;
    }
  }

  private static trimTrailingEmptyTexts(
    texts: string[] | null,
    targetLength: number,
  ): string[] | null {
    if (!texts) return null;
    const arr = [...texts];
    while (arr.length > targetLength) {
      if ((arr[arr.length - 1] || '').trim().length > 0) break;
      arr.pop();
    }
    return arr;
  }

  /** Latin / digit phrases that stay contiguous even when Hebrew redlines interleave. */
  private static collectDistinctiveSearchAnchors(searchNorm: string): string[] {
    const out: string[] = [];
    const seen = new Set<string>();
    const add = (raw: string) => {
      const t = (raw || '').replace(/\s+/g, ' ').trim();
      if (t.length >= 12 && !seen.has(t)) {
        seen.add(t);
        out.push(t);
      }
    };
    const latin = searchNorm.match(/[A-Za-z][A-Za-z0-9/.\- ]{11,}/g) || [];
    for (const m of latin) add(m);
    if (searchNorm.length >= 40) add(searchNorm.slice(-40));
    if (searchNorm.length >= 80) add(searchNorm.slice(-80));
    out.sort((a, b) => b.length - a.length);
    return out;
  }

  private static findIndexByDistinctiveAnchors(
    haystacks: string[],
    searchNorm: string,
  ): number {
    const anchors = DocumentReader.collectDistinctiveSearchAnchors(searchNorm);
    for (const anchor of anchors) {
      const hits: number[] = [];
      for (let i = 0; i < haystacks.length; i++) {
        if (haystacks[i]?.includes(anchor)) hits.push(i);
      }
      if (hits.length === 1) return hits[0];
    }
    return -1;
  }

  private static paragraphSharesDistinctiveAnchor(paraNorm: string, searchNorm: string): boolean {
    if (!paraNorm || !searchNorm) return false;
    return DocumentReader.collectDistinctiveSearchAnchors(searchNorm).some(
      (anchor) => paraNorm.includes(anchor),
    );
  }

  /**
   * Navigate using backend/engine paragraph_index (OOXML document order, includes table cells).
   */
  static async navigateToDocumentParagraphIndex(
    paragraphIndex: number,
    debugContext?: NavigationDebugContext,
    anchorTextOverride?: string,
  ): Promise<boolean> {
    if (!isWordAvailable() || paragraphIndex < 0 || Number.isNaN(paragraphIndex)) {
      return false;
    }

    return DocumentReader.runNavigationQueued(async () => {
      try {
        const direct = await Word.run(async (context) =>
          DocumentReader.selectParagraphAtEngineIndex(context, paragraphIndex),
        );
        if (direct) {
          return true;
        }
      } catch (error) {
        logError('navigateToDocumentParagraphIndex: direct index select failed', error);
      }

      const allTexts = await DocumentReader.getCachedOoxmlParagraphTexts();
      if (paragraphIndex >= allTexts.length) {
        debugWarn('DocumentReader.navigateToDocumentParagraphIndex: index out of range', {
          paragraphIndex,
          totalParagraphs: allTexts.length,
          ...(debugContext ?? {}),
        });
        return false;
      }

      const ooxmlText = (allTexts[paragraphIndex] || '').replace(/\s+/g, ' ').trim();
      const override = (anchorTextOverride || '').replace(/\s+/g, ' ').trim();
      const ooxmlNorm = DocumentReader.normalizeForMatch(ooxmlText);

      const searchCandidates: string[] = [];
      const seen = new Set<string>();
      for (const candidate of [ooxmlText, override]) {
        const key = DocumentReader.normalizeForMatch(candidate);
        if (key.length >= 10 && !seen.has(key)) {
          seen.add(key);
          searchCandidates.push(candidate);
        }
      }

      for (const text of searchCandidates) {
        const ok = await DocumentReader.navigateToParagraphByContentImpl(text, {
          ...(debugContext ?? {}),
          strategy: 'ooxml_paragraph_index',
          ooxmlParagraphIndex: paragraphIndex,
          skipOoxmlScanFallback: true,
          ooxmlTargetNorm: ooxmlNorm,
        });
        if (ok) {
          return true;
        }
      }

      return false;
    });
  }

  /**
   * Select body.paragraphs[index] only when its live text is a strong match for
   * one of the clause search strings. One Word.run — no OOXML, no table walk.
   * Wrong twins (9.2 vs 9.4) and leftover cells fail the score and return false.
   */
  static async navigateToParagraphIndexIfContentMatches(
    paragraphIndex: number,
    searchTexts: string[],
    _debugContext?: NavigationDebugContext,
  ): Promise<boolean> {
    if (!isWordAvailable() || paragraphIndex < 0 || Number.isNaN(paragraphIndex)) {
      return false;
    }
    const searchNorms = searchTexts
      .map((text) => DocumentReader.normalizeForMatch(text || ''))
      .filter((norm) => norm.length >= MIN_CLAUSE_SEARCH_LEN);
    if (searchNorms.length === 0) {
      return false;
    }

    return DocumentReader.runNavigationQueued(async () => {
      try {
        return await Word.run(async (context) => {
          const paragraphs = context.document.body.paragraphs;
          paragraphs.load('items');
          await context.sync();
          if (paragraphIndex >= paragraphs.items.length) {
            return false;
          }
          const para = paragraphs.items[paragraphIndex];
          para.load('text');
          await context.sync();
          const paraNorm = DocumentReader.normalizeForMatch(para.text || '');
          if (!paragraphMatchesAnySearch(paraNorm, searchNorms)) {
            return false;
          }
          para.select();
          await context.sync();
          return true;
        });
      } catch (error) {
        logError('navigateToParagraphIndexIfContentMatches failed', error);
        return false;
      }
    });
  }

  /**
   * Extracts all track-change paragraphs from the live Word document using Office.js.
   *
   * Uses body.getOoxml() (not body.load('ooxml') which is NOT a valid loadable property
   * on Body objects).  getOoxml() returns a package XML wrapper; we unwrap it with
   * extractParagraphXmlFromPackage to get the actual <w:body> content, then parse all
   * <w:p> elements — including those nested inside table cells.
   *
   * Returns:
   *  allParagraphTexts  — plain text of every <w:p> in document order (body + tables).
   *  paragraphsWithTrackChanges — enriched data for paragraphs that contain w:ins or w:del.
   */
  static async extractTrackChangedParagraphs(): Promise<{
    allParagraphTexts: string[];
    paragraphsWithTrackChanges: Array<{
      paragraphIndex: number;
      paragraphXml: string;
      originalText: string;
      editedText: string;
      participants: string[];
    }>;
  }> {
    if (!isWordAvailable()) {
      return { allParagraphTexts: [], paragraphsWithTrackChanges: [] };
    }

    try {
      return await Word.run(async (context) => {
        // body.getOoxml() is the correct API — returns a ClientResult<string>.
        // body.load('ooxml') does NOT work on Body objects.
        const ooxmlResult = (context.document.body as any).getOoxml();
        await context.sync();

        const packageXml: string = ooxmlResult.value ?? '';
        if (!packageXml) {
          log('extractTrackChangedParagraphs: body.getOoxml() returned empty');
          return { allParagraphTexts: [], paragraphsWithTrackChanges: [] };
        }

        // Unwrap the OPC package to get the <w:body> (or full document.xml) content
        const bodyXml = this.extractParagraphXmlFromPackage(packageXml);
        if (!bodyXml) {
          log('extractTrackChangedParagraphs: extractParagraphXmlFromPackage returned empty');
          return { allParagraphTexts: [], paragraphsWithTrackChanges: [] };
        }

        log(`extractTrackChangedParagraphs: packageXml length=${packageXml.length}, bodyXml length=${bodyXml.length}`);

        // ── Parse the body XML (includes table cells) ───────────────────────
        const domParser = new DOMParser();
        const xmlDoc = domParser.parseFromString(bodyXml, 'text/xml');

        if (xmlDoc.querySelector('parsererror')) {
          logError('extractTrackChangedParagraphs: body XML failed to parse');
          return { allParagraphTexts: [], paragraphsWithTrackChanges: [] };
        }

        const NS_W = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
        const serializer = new XMLSerializer();

        // All <w:p> in document order – body paragraphs AND table-cell paragraphs
        const allPEls = Array.from(xmlDoc.getElementsByTagNameNS(NS_W, 'p'));

        const allParagraphTexts: string[] = [];
        const paragraphsWithTrackChanges: Array<{
          paragraphIndex: number;
          paragraphXml: string;
          originalText: string;
          editedText: string;
          participants: string[];
        }> = [];

        for (let i = 0; i < allPEls.length; i++) {
          const pEl = allPEls[i];

          // ── Plain text: all w:t content, including inside w:ins and w:del ──
          const tEls = Array.from(pEl.getElementsByTagNameNS(NS_W, 't'));
          const text = tEls.map((t) => t.textContent ?? '').join('');
          allParagraphTexts.push(text);

          // ── Track-change detection via DOM ────────────────────────────────
          const insEls = Array.from(pEl.getElementsByTagNameNS(NS_W, 'ins'));
          const delEls = Array.from(pEl.getElementsByTagNameNS(NS_W, 'del'));
          if (insEls.length === 0 && delEls.length === 0) continue;

          // Serialize paragraph XML and ensure w: namespace is declared
          let pXml = serializer.serializeToString(pEl);
          if (!pXml.includes('xmlns:w=')) {
            pXml = pXml.replace(/^(<[^>]+)>/, `$1 xmlns:w="${NS_W}">`);
          }

          // Collect unique authors from w:ins/@w:author and w:del/@w:author
          const getAuthor = (el: Element) =>
            el.getAttributeNS(NS_W, 'author') || el.getAttribute('w:author') || '';
          const participants = [
            ...new Set([...insEls.map(getAuthor), ...delEls.map(getAuthor)].filter(Boolean)),
          ];

          paragraphsWithTrackChanges.push({
            paragraphIndex: i,
            paragraphXml: pXml,
            originalText: TrackChangesParser.extractOriginalText(pXml),
            editedText: TrackChangesParser.extractAcceptedText(pXml),
            participants,
          });
        }

        log(
          `extractTrackChangedParagraphs: ${paragraphsWithTrackChanges.length} TC paragraphs ` +
          `out of ${allParagraphTexts.length} total (body + table cells)`
        );
        return { allParagraphTexts, paragraphsWithTrackChanges };
      });
    } catch (error) {
      logError('Failed to extract track changed paragraphs', error);
      return { allParagraphTexts: [], paragraphsWithTrackChanges: [] };
    }
  }

  /** Normalize text the same way paragraph sync / content navigation does. */
  static normalizeSearchTextForNavigation(text: string): string {
    return DocumentReader.normalizeForMatch(text);
  }

  /**
   * Navigates to a specific paragraph by index
   * Uses direct selection which automatically navigates Word to that location
   * @returns true when a non-empty paragraph was selected, false otherwise
   */
  static async navigateToParagraph(paragraphIndex: number, _debugContext?: Record<string, unknown>): Promise<boolean> {
    return DocumentReader.runNavigationQueued(async () => {
    if (!isWordAvailable()) {
      debugWarn('DocumentReader.navigateToParagraph: Word API unavailable', { paragraphIndex });
      return false;
    }

    try {
      return await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        const paragraphCount = paragraphs.items.length;

        if (paragraphIndex >= 0 && paragraphIndex < paragraphCount) {
          const paragraph = paragraphs.items[paragraphIndex];

          paragraph.load('text');
          await context.sync();

          const paragraphText = paragraph.text.trim();
          if (!paragraphText) {
            debugWarn('DocumentReader.navigateToParagraph: empty paragraph', { paragraphIndex, paragraphCount });
            return false;
          }

          paragraph.select();
          await context.sync();
          return true;
        }

        debugWarn('DocumentReader.navigateToParagraph: index out of range', {
          paragraphIndex,
          paragraphCount,
        });
        return false;
      });
    } catch (error) {
      logError('Failed to navigate to paragraph', error);
      return false;
    }
    });
  }

  /** Remove common Word field-code noise (e.g. REF ... MERGEFORMAT); same rules as Ask OOXML path. */
  private static stripWordFieldCodes(text: string): string {
    return TrackChangesParser.stripRefFieldNoise(text || '');
  }

  /** Normalize text for comparison: remove field-codes, collapse whitespace and trim. */
  private static normalizeForMatch(text: string): string {
    return DocumentReader.stripWordFieldCodes(text || '')
      .replace(/[\u0005\u0007]/g, '')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[\u2018\u2019\u2032]/g, "'")
      .replace(/[\u201C\u201D\u2033]/g, '"')
      .replace(/[\u2013\u2014]/g, '-')
      .replace(/^\s*בסעיף\s+[^\-–—]+[-–—]\s*/i, '')
      // Strip bare leading clause numbers that Word renders via auto-numbering
      // (so paragraph.text does NOT contain the number). Examples we strip:
      //   "4.4 …", "4.4. …", "4.4.1 …", "4.4)\u00A0…", "‏4.4 …" (with RTL marks).
      .replace(/^[\s\u200E\u200F\u061C]*\d+(?:\.\d+){0,4}[.)]?[\s\u00A0\u200E\u200F\u061C]+/, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Match search text to a body paragraph index (exact, then paragraph-contains-search, then tail).
   * Never uses searchNorm.includes(shortParagraph) — that falsely matches empty paragraph 0.
   * Skips indices already assigned to another clause when `usedIndices` is provided.
   */
  private static findParagraphIndexForNormalizedSearch(
    normalizedParagraphs: string[],
    searchNorm: string,
    usedIndices?: Set<number>,
  ): number {
    const available = normalizedParagraphs.map((p, index) =>
      usedIndices?.has(index) ? '' : p,
    );
    const picked = pickBestParagraphIndex(available, searchNorm);
    return isAcceptableParagraphSearchPick(picked.score, searchNorm) ? picked.index : -1;
  }

  /** Whether normalized paragraph text is a strong match (not a shared-prefix twin). */
  private static paragraphMatchesSearchNorm(paraNorm: string, searchNorm: string): boolean {
    return isStrongParagraphSearchMatch(paraNorm, searchNorm);
  }

  /** Whether a paragraph matches primary search text or any table-row anchor. */
  private static paragraphMatchesSearchOrAnchors(
    paraNorm: string,
    searchNorm: string,
    searchAnchors?: string[],
  ): boolean {
    if (DocumentReader.paragraphMatchesSearchNorm(paraNorm, searchNorm)) {
      return true;
    }
    if (
      searchNorm.length > 0 &&
      searchNorm.length < 40 &&
      (paraNorm === searchNorm || paraNorm.includes(searchNorm))
    ) {
      return true;
    }
    if (!searchAnchors?.length) {
      return false;
    }
    for (const anchor of searchAnchors) {
      const anchorNorm = DocumentReader.normalizeForMatch(anchor);
      if (!anchorNorm) continue;
      if (DocumentReader.paragraphMatchesSearchNorm(paraNorm, anchorNorm)) {
        return true;
      }
      if (anchorNorm.length < 40 && paraNorm.includes(anchorNorm)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Clause numbers often appear only on the first paragraph of a multi-paragraph clause.
   * Walk backward from idx to find listString or leading "4.5" / "4.5." in paragraph text.
   */
  private static resolveClauseNumberAtOrBeforeIndex(
    idx: number,
    paragraphTexts: string[],
    listStrings: (string | undefined)[],
    leadingNumberRe: RegExp,
    maxLookback = 8,
  ): string | undefined {
    const start = Math.max(0, idx - maxLookback);
    for (let i = idx; i >= start; i--) {
      const listStr = listStrings[i];
      if (listStr) {
        return listStr.replace(/\.$/, '');
      }
      const text = paragraphTexts[i] || '';
      const match = text.match(leadingNumberRe);
      if (match?.[1]) {
        return match[1];
      }
    }
    return undefined;
  }

  /**
   * Resolve the clause list number for a body paragraph (walks backward for multi-para clauses).
   */
  static async getClauseNumberForParagraphIndex(
    paragraphIndex: number,
    maxLookback = 8,
  ): Promise<string | null> {
    if (!isWordAvailable() || paragraphIndex < 0) {
      return null;
    }

    try {
      return await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        const n = paragraphs.items.length;
        if (paragraphIndex >= n) {
          return null;
        }

        const start = Math.max(0, paragraphIndex - maxLookback);
        const end = paragraphIndex + 1;
        const windowTexts: string[] = [];
        const windowListStrings: (string | undefined)[] = [];

        for (let i = start; i < end; i++) {
          const para = paragraphs.items[i] as any;
          para.load('text');
          try {
            const li = para.listItemOrNullObject;
            if (li) {
              li.load('listString,isNullObject');
            }
          } catch {
            // ignore
          }
        }
        await context.sync();

        for (let i = start; i < end; i++) {
          const para = paragraphs.items[i] as any;
          windowTexts.push(para.text || '');
          let listString: string | undefined;
          try {
            const li = para.listItemOrNullObject;
            if (li && !li.isNullObject && li.listString != null && String(li.listString).trim() !== '') {
              listString = String(li.listString).trim();
            }
          } catch {
            // ignore
          }
          windowListStrings.push(listString);
        }

        const relativeIdx = paragraphIndex - start;
        const leadingNumberRe = /^\s*(\d+(?:\.\d+)*)[.\s]/;
        return (
          DocumentReader.resolveClauseNumberAtOrBeforeIndex(
            relativeIdx,
            windowTexts,
            windowListStrings,
            leadingNumberRe,
            maxLookback,
          ) ?? null
        );
      });
    } catch (error) {
      logError('Failed to resolve clause number for paragraph', error);
      return null;
    }
  }

  /**
   * Word.js `body.search` rejects strings over 255 characters (`SearchStringInvalidOrTooLong`).
   * Stay under the limit when using the Search API; use paragraph scanning for full long text.
   */
  private static readonly WORD_BODY_SEARCH_MAX_CHARS = 250;

  /** Collapse smart quotes/dashes and strip Word search wildcards for body.search. */
  private static normalizeForBodySearch(text: string): string {
    return text
      .replace(/[\u2018\u2019\u2032]/g, "'")
      .replace(/[\u201C\u201D\u2033]/g, '"')
      .replace(/[\u2013\u2014]/g, '-')
      .replace(/["'`׳״]/g, ' ')
      .replace(/[()[\]<>?*\\^]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** Build search attempt strings (full + distinctive tail, then heads), capped at 255 chars. */
  private static buildBodySearchAttempts(searchNorm: string): string[] {
    const attempts: string[] = [];
    const seen = new Set<string>();
    const add = (value: string) => {
      const cleaned = DocumentReader.normalizeForBodySearch(value).slice(
        0,
        DocumentReader.WORD_BODY_SEARCH_MAX_CHARS,
      );
      if (cleaned.length >= 20 && !seen.has(cleaned)) {
        seen.add(cleaned);
        attempts.push(cleaned);
      }
    };

    for (const slice of distinctiveBodySearchSlices(searchNorm)) {
      add(slice);
    }

    return attempts;
  }

  /**
   * Finds the first body paragraph index whose normalized text matches the search text, or contains it
   * (or a distinctive prefix), using the same heuristics as {@link syncParagraphIndicesFromDocument}.
   * Does not use `body.search`, so there is no 255-character limit on `searchText`.
   */
  static async findParagraphIndexContainingText(searchText: string): Promise<number | null> {
    const hit = await DocumentReader.scanLiveParagraphsForApply(searchText, false);
    return hit?.index ?? null;
  }

  /**
   * One Word.run: load every body paragraph text, pick the search hit, optionally
   * select it, and return the live folded text. Used by locate-only and by
   * resolveSelectAndReadForApply so submit does not pay three collection loads.
   */
  private static async scanLiveParagraphsForApply(
    searchText: string,
    selectHit: boolean,
  ): Promise<{ index: number; liveText: string; selected: boolean } | null> {
    if (!isWordAvailable() || !searchText || !searchText.trim()) {
      return null;
    }

    try {
      return await Word.run(async (context) => {
        const t0 = Date.now();
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();
        const loadItemsMs = Date.now() - t0;
        const paraCount = paragraphs.items.length;

        const tTexts = Date.now();
        for (let i = 0; i < paragraphs.items.length; i++) {
          paragraphs.items[i].load('text');
        }
        await context.sync();
        const loadTextsMs = Date.now() - tTexts;

        const searchNorm = DocumentReader.normalizeForMatch(searchText);
        if (!searchNorm) {
          logQuickResponseStage('profile: live-text scan empty searchNorm', {
            loadItemsMs,
            loadTextsMs,
            paraCount,
          });
          return null;
        }

        const normalizedParagraphs: string[] = [];
        for (let i = 0; i < paragraphs.items.length; i++) {
          normalizedParagraphs.push(
            DocumentReader.normalizeForMatch(paragraphs.items[i].text || ''),
          );
        }

        let exactIdx = -1;
        let exactHits = 0;
        for (let i = 0; i < normalizedParagraphs.length; i++) {
          if (normalizedParagraphs[i] === searchNorm) {
            exactHits += 1;
            if (exactIdx < 0) exactIdx = i;
          }
        }
        const picked = pickBestParagraphIndex(normalizedParagraphs, searchNorm);
        const fuzzyIdx = DocumentReader.findParagraphIndexForNormalizedSearch(
          normalizedParagraphs,
          searchNorm,
        );
        const index = exactIdx >= 0 ? exactIdx : fuzzyIdx >= 0 ? fuzzyIdx : -1;
        logQuickResponseStage('profile: live-text scan', {
          searchLen: searchNorm.length,
          paraCount,
          loadItemsMs,
          loadTextsMs,
          exactHits,
          exactIdx,
          bestScore: picked.score,
          bestIndex: picked.index,
          bestLen: picked.index >= 0 ? (normalizedParagraphs[picked.index] || '').length : 0,
          accepted: index >= 0,
          selectHit,
          totalMs: Date.now() - t0,
        });
        if (index < 0) return null;

        const liveText = (paragraphs.items[index].text || '').replace(/\s+/g, ' ').trim();
        if (!selectHit) {
          return { index, liveText, selected: false };
        }
        try {
          paragraphs.items[index].select();
          await context.sync();
          return { index, liveText, selected: true };
        } catch {
          return { index, liveText, selected: false };
        }
      });
    } catch (error) {
      logQuickResponseStage('profile: live-text scan FAILED', {
        message: error instanceof Error ? error.message : String(error),
      });
      logError('Failed to find paragraph index containing text', error);
      return null;
    }
  }

  /**
   * Finds the body.paragraphs index whose text matches the given content (normalized).
   * Returns null if not found (e.g. when the paragraph is inside a table).
   */
  static async findParagraphIndexByContent(searchText: string): Promise<number | null> {
    if (!isWordAvailable() || !searchText || !searchText.trim()) {
      return null;
    }

    try {
      return await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        const searchNorm = DocumentReader.normalizeForMatch(searchText);

        for (let i = 0; i < paragraphs.items.length; i++) {
          const para = paragraphs.items[i];
          para.load('text');
        }
        await context.sync();

        for (let i = 0; i < paragraphs.items.length; i++) {
          const paraText = paragraphs.items[i].text || '';
          if (DocumentReader.normalizeForMatch(paraText) === searchNorm) {
            return i;
          }
        }
        return null;
      });
    } catch (error) {
      logError('Failed to find paragraph index by content', error);
      return null;
    }
  }

  private static resolveBodyIndexForClauseNumber(
    syncIdx: number,
    useOoxml: boolean,
    normalizedParagraphs: string[],
    paragraphTexts: string[],
  ): number {
    if (syncIdx < 0) return -1;

    const normalizedBodyParagraphs = paragraphTexts.map((t) =>
      DocumentReader.normalizeForMatch(t),
    );

    if (!useOoxml) {
      return syncIdx < paragraphTexts.length ? syncIdx : -1;
    }

    if (
      syncIdx < paragraphTexts.length &&
      syncIdx < normalizedParagraphs.length &&
      DocumentReader.paragraphMatchesSearchNorm(
        normalizedBodyParagraphs[syncIdx],
        normalizedParagraphs[syncIdx],
      )
    ) {
      return syncIdx;
    }

    const targetNorm = normalizedParagraphs[syncIdx];
    if (!targetNorm) {
      return syncIdx < paragraphTexts.length ? syncIdx : -1;
    }

    const bodyIdx = DocumentReader.findParagraphIndexForNormalizedSearch(
      normalizedBodyParagraphs,
      targetNorm,
    );
    return bodyIdx >= 0 ? bodyIdx : syncIdx < paragraphTexts.length ? syncIdx : -1;
  }

  /**
   * indices: clause id -> body.paragraphs index
   * clauseNumbers: clause id -> list number string (e.g. "8.4") when the paragraph is in a numbered list
   */
  static async syncParagraphIndicesFromDocument(
    entries: Array<{
      id: string;
      searchText: string;
      existingIndex?: number;
      isTableRow?: boolean;
      paragraphIndices?: number[];
      searchAnchors?: string[];
    }>
  ): Promise<{ indices: Record<string, number>; clauseNumbers: Record<string, string> }> {
    const indices: Record<string, number> = {};
    const clauseNumbers: Record<string, string> = {};
    if (!isWordAvailable() || entries.length === 0) {
      return { indices, clauseNumbers };
    }

    const withText = entries.filter((e) => e.searchText && e.searchText.trim().length > 0);
    if (withText.length === 0) {
      return { indices, clauseNumbers };
    }

    try {
      const ooxmlParagraphTexts = await DocumentReader.getOoxmlAllParagraphTexts();
      const useOoxml = ooxmlParagraphTexts.length > 0;

      const { texts: paragraphTexts, listStrings } = await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        const n = paragraphs.items.length;
        const batch = DocumentReader.PARAGRAPH_TEXT_LOAD_BATCH_SIZE;
        const texts: string[] = new Array(n).fill('');
        const listStringsOut: (string | undefined)[] = new Array(n).fill(undefined);

        for (let start = 0; start < n; start += batch) {
          const end = Math.min(start + batch, n);
          for (let i = start; i < end; i++) {
            const para = paragraphs.items[i] as any;
            para.load('text');
            try {
              const li = para.listItemOrNullObject;
              if (li) {
                li.load('listString,isNullObject');
              }
            } catch {
              // listItemOrNullObject may be unavailable in this host
            }
          }
          await context.sync();

          for (let i = start; i < end; i++) {
            const para = paragraphs.items[i] as any;
            texts[i] = para.text || '';
            try {
              const li = para.listItemOrNullObject;
              if (li && !li.isNullObject && li.listString != null && String(li.listString).trim() !== '') {
                listStringsOut[i] = String(li.listString).trim();
              }
            } catch {
              // ignore
            }
          }
        }

        return { texts, listStrings: listStringsOut };
      });

      const syncParagraphTexts = useOoxml ? ooxmlParagraphTexts : paragraphTexts;
      const normalizedParagraphs = syncParagraphTexts.map((t) =>
        DocumentReader.normalizeForMatch(t)
      );

      const leadingNumberRe = /^\s*(\d+(?:\.\d+)*)[.\s]/;
      const matchDetails: Array<Record<string, unknown>> = [];
      const unmatched: Array<Record<string, unknown>> = [];
      const usedIndices = new Set<number>();

      const findIndexForEntry = (
        searchNorm: string,
        used: Set<number>,
        anchors?: string[],
      ): number => {
        const tryNorm = (norm: string): number => {
          if (!norm) return -1;
          return DocumentReader.findParagraphIndexForNormalizedSearch(
            normalizedParagraphs,
            norm,
            used,
          );
        };
        let idx = tryNorm(searchNorm);
        if (idx >= 0) return idx;
        if (anchors?.length) {
          for (const anchor of anchors) {
            idx = tryNorm(DocumentReader.normalizeForMatch(anchor));
            if (idx >= 0) return idx;
          }
        }
        return -1;
      };

      for (const { id, searchText, existingIndex, isTableRow, paragraphIndices, searchAnchors } of withText) {
        const searchNorm = DocumentReader.normalizeForMatch(searchText);
        if (!searchNorm) {
          unmatched.push({
            clauseId: id,
            searchSnippet: '',
            searchLen: 0,
            reason: 'empty_search_norm',
          });
          continue;
        }

        let idx = -1;
        let matchSource: 'existing' | 'sync' | 'table_row_index' | undefined;

        const candidateIndices = [
          ...(existingIndex != null && !Number.isNaN(existingIndex) ? [existingIndex] : []),
          ...(Array.isArray(paragraphIndices) ? paragraphIndices : []),
        ].filter((n, i, arr) => typeof n === 'number' && arr.indexOf(n) === i);

        // Table rows: match by TC request text first — backend indices can be stale
        // after a rerun or may reference a clause-ref cell in another row.
        if (isTableRow && useOoxml) {
          idx = findIndexForEntry(searchNorm, usedIndices, searchAnchors);
          if (idx >= 0) {
            matchSource = 'sync';
          }
        }

        if (idx < 0 && isTableRow && useOoxml && candidateIndices.length > 0) {
          for (const candidate of candidateIndices) {
            if (
              candidate >= 0 &&
              candidate < normalizedParagraphs.length &&
              !usedIndices.has(candidate) &&
              DocumentReader.paragraphMatchesSearchOrAnchors(
                normalizedParagraphs[candidate],
                searchNorm,
                searchAnchors,
              )
            ) {
              idx = candidate;
              matchSource = 'table_row_index';
              break;
            }
          }
        }

        if (
          idx < 0 &&
          existingIndex != null &&
          !Number.isNaN(existingIndex) &&
          existingIndex >= 0 &&
          existingIndex < normalizedParagraphs.length &&
          !usedIndices.has(existingIndex) &&
          DocumentReader.paragraphMatchesSearchOrAnchors(
            normalizedParagraphs[existingIndex],
            searchNorm,
            isTableRow ? searchAnchors : undefined,
          )
        ) {
          idx = existingIndex;
          matchSource = 'existing';
        } else if (idx < 0) {
          idx = findIndexForEntry(searchNorm, usedIndices, searchAnchors);
          if (idx >= 0) {
            matchSource = 'sync';
          }
        }

        if (idx >= 0) {
          usedIndices.add(idx);
          indices[id] = idx;
          const bodyIdxForNumber = DocumentReader.resolveBodyIndexForClauseNumber(
            idx,
            useOoxml,
            normalizedParagraphs,
            paragraphTexts,
          );
          const resolvedNumber =
            bodyIdxForNumber >= 0
              ? DocumentReader.resolveClauseNumberAtOrBeforeIndex(
                  bodyIdxForNumber,
                  paragraphTexts,
                  listStrings,
                  leadingNumberRe,
                )
              : undefined;
          if (resolvedNumber) {
            clauseNumbers[id] = resolvedNumber;
          }
          matchDetails.push({
            clauseId: id,
            paragraphIndex: idx,
            clauseNumber: resolvedNumber ?? null,
            matchSource: matchSource ?? null,
            isTableRow: isTableRow ?? false,
            usedOoxml: useOoxml,
            searchSnippet: searchNorm.slice(0, 100),
            paragraphSnippet: normalizedParagraphs[idx]?.slice(0, 100) ?? '',
          });
        } else {
          unmatched.push({
            clauseId: id,
            searchSnippet: searchNorm.slice(0, 120),
            searchLen: searchNorm.length,
            isTableRow: isTableRow ?? false,
            anchorCount: searchAnchors?.length ?? 0,
          });
        }
      }

      const clauseNumberCorrections = Object.entries(clauseNumbers).length;
      debugLog('DocumentReader: paragraph sync', {
        matched: Object.keys(indices).length,
        entries: withText.length,
        clauseNumbersResolved: clauseNumberCorrections,
        usedOoxml: useOoxml,
        ooxmlParagraphCount: ooxmlParagraphTexts.length,
        bodyParagraphCount: paragraphTexts.length,
      });
      debugLog('DocumentReader: paragraph sync detail', {
        bodyParagraphCount: paragraphTexts.length,
        matched: Object.keys(indices).length,
        unmatchedCount: unmatched.length,
        unmatched,
        matches: matchDetails,
      });

      return { indices, clauseNumbers };
    } catch (error) {
      logError('Failed to sync paragraph indices from document', error);
      return { indices, clauseNumbers };
    }
  }

  /**
   * Navigates to the paragraph that contains the given text by searching the document.
   * Works for content in body paragraphs and inside tables (body.search searches document content).
   * Selects the whole paragraph containing the match.
   *
   * Long strings: Word's Search API allows at most 255 characters — we truncate for `body.search`
   * and fall back to scanning body paragraphs when search fails or finds nothing.
   */
  static async navigateToParagraphByContent(
    searchText: string,
    debugContext?: NavigationDebugContext,
  ): Promise<boolean> {
    return DocumentReader.runNavigationQueued(() =>
      DocumentReader.navigateToParagraphByContentImpl(searchText, debugContext),
    );
  }

  private static async navigateToParagraphByContentImpl(
    searchText: string,
    debugContext?: NavigationDebugContext,
  ): Promise<boolean> {
    const trimmed = searchText?.trim() || '';
    const searchNorm = DocumentReader.normalizeForMatch(trimmed);

    if (!isWordAvailable() || !trimmed) {
      return false;
    }

    if (!searchNorm) {
      return false;
    }

    // body.search treats ( ) ? * < > [ ] \ ^ as wildcards and chokes on long
    // strings with quotes/parens. Build multiple clean prefixes to maximize hit rate.
    let searchAttempts = DocumentReader.buildBodySearchAttempts(searchNorm);
    const maxAttempts = debugContext?.maxSearchAttempts;
    if (typeof maxAttempts === 'number' && maxAttempts > 0 && maxAttempts < searchAttempts.length) {
      // Keep longest prefixes. Reversing to 40/60/80-char heads matched twin clauses (9.2 vs 9.4).
      searchAttempts = searchAttempts.slice(0, maxAttempts);
    }

    let navigatedViaSearch = false;

    const selectFullParagraph = debugContext?.selectFullParagraph !== false;
    const ooxmlIdx =
      typeof debugContext?.ooxmlParagraphIndex === 'number'
        ? debugContext.ooxmlParagraphIndex
        : null;
    let ooxmlTargetNorm =
      typeof debugContext?.ooxmlTargetNorm === 'string'
        ? debugContext.ooxmlTargetNorm
        : '';

    const selectSearchHit = (range: Word.Range, selectFull: boolean) => {
      const paragraph = range.paragraphs.getFirst();
      if (selectFull) {
        paragraph.select();
      } else {
        // Table cells: narrow range avoids highlighting the entire cell.
        range.select();
      }
    };

    for (const attempt of searchAttempts) {
      if (navigatedViaSearch) break;
      let attemptMatchCount = 0;

      const runSearchAndSelect = async (): Promise<boolean> => {
        let selected = false;
        await Word.run(async (context) => {
          const body = context.document.body;
          const searchResults = body.search(attempt, {
            matchCase: false,
            matchWholeWord: false,
          });
          searchResults.load('items');
          await context.sync();

          attemptMatchCount = searchResults.items.length;

          if (attemptMatchCount === 0) {
            return;
          }

          const matchParagraphs = searchResults.items.map((range) => range.paragraphs.getFirst());
          matchParagraphs.forEach((paragraph) => paragraph.load('text'));
          await context.sync();

          const hitNorms = matchParagraphs.map((paragraph) =>
            DocumentReader.normalizeForMatch(paragraph.text || ''),
          );
          const picked = pickBestParagraphIndex(hitNorms, searchNorm, ooxmlTargetNorm || undefined);
          if (picked.index < 0 || !isAcceptableParagraphSearchPick(picked.score, searchNorm)) {
            return;
          }

          selectSearchHit(searchResults.items[picked.index], selectFullParagraph);
          selected = true;
          await context.sync();
        });
        return selected;
      };

      try {
        if (await runSearchAndSelect()) {
          navigatedViaSearch = true;
          break;
        }

        if (
          !debugContext?.skipParagraphScanFallback &&
          attemptMatchCount > 1 &&
          ooxmlIdx != null &&
          ooxmlIdx >= 0 &&
          !ooxmlTargetNorm
        ) {
          const allTexts = await DocumentReader.getCachedOoxmlParagraphTexts();
          if (ooxmlIdx < allTexts.length) {
            ooxmlTargetNorm = DocumentReader.normalizeForMatch(allTexts[ooxmlIdx] || '');
          }
          if (ooxmlTargetNorm && (await runSearchAndSelect())) {
            navigatedViaSearch = true;
            break;
          }
        }
      } catch (error) {
        logError('Failed to navigate to paragraph by content (search)', error);
      }
    }

    if (navigatedViaSearch) {
      return true;
    }

    if (debugContext?.skipParagraphScanFallback) {
      return false;
    }

    const idx = await DocumentReader.findParagraphIndexContainingText(searchNorm);

    if (idx != null) {
      return DocumentReader.navigateToParagraph(idx, {
        ...debugContext,
        via: 'content_scan_fallback',
        searchNormSnippet: searchNorm.slice(0, 120),
      });
    }

    if (!debugContext?.skipOoxmlScanFallback) {
      const ooxmlMatchIdx = await DocumentReader.findOoxmlParagraphIndexContainingText(searchNorm);
      if (ooxmlMatchIdx != null) {
        try {
          const selected = await Word.run(async (context) =>
            DocumentReader.selectParagraphAtEngineIndex(context, ooxmlMatchIdx),
          );
          if (selected) {
            return true;
          }
        } catch (error) {
          logError('navigateToParagraphByContent: OOXML index select failed', error);
        }
      }
    }

    debugWarn('DocumentReader.navigateToParagraphByContent: no match found', {
      searchSnippet: searchNorm.slice(0, 120),
      searchLen: searchNorm.length,
      ...(debugContext ?? {}),
    });
    return false;
  }
}

